<template>
	<Pupup
		@close="close()"
		:showClose="true"
		:width="800"
		:shuffleUp="true"
		:clickOverlayToClose="false"
		:headerNoMarginBottom="true"
		:scrollTop="scrollTrigger"
		:noScroll="!!loadingMessage && loadingMessageSource !== 'brief-writer'"
		:class="{ 'brand-connect-main-pupup': true, 'brand-connect-main-pupup--request': isRequest, 'pitch-assistant-open': isPitchAssistantOpen }"
		key="pupup-brand-connect">
		<template v-slot:header>
			<h3 v-if="isRequest" class="brand-connect-header-title">A request to collab</h3>
			<div class="brand-connect-header">
				<div class="brand-connect-cache-header">
					<div
						class="brand-connect-cache-badge"
						v-if="cacheBadgeText"
						v-show="showCacheStatusUi"
						:class="{ 'brand-connect-cache-badge--has-tooltip': cacheBadgeHasTooltip && cacheBadgeTooltipReady }"
						:tabindex="cacheBadgeHasTooltip ? 0 : undefined"
						:aria-describedby="cacheBadgeHasTooltip ? cacheBadgeTooltipId : undefined"
						@mouseenter="onCacheBadgeEnter"
						@mouseleave="armCacheBadgeTooltip">
						{{ cacheBadgeText }}
						<span
							v-if="cacheBadgeHasTooltip && cacheBadgeTooltipReady"
							:id="cacheBadgeTooltipId"
							class="brand-connect-cache-tooltip"
							role="tooltip">
							This content has been saved so you can close this window and come back to it later if you need to.
						</span>
					</div>
					<div class="brand-connect-cache-controls">
						<button
							v-if="cacheDirty"
							v-show="showCacheStatusUi"
							type="button"
							class="brand-connect-cache-save"
							:disabled="cacheSaving"
							@click="saveCacheManually">
							💾 Save
						</button>
						<button
							class="brand-connect-cache-control-button"
							type="button"
							:disabled="!canHistoryUndo || !!loadingMessage"
							aria-label="Undo"
							aria-describedby="brand-connect-cache-tooltip-undo"
							@click="undoHistory"
							@mouseenter="onHistoryTooltipEnter('undo')"
							@mouseleave="onHistoryTooltipLeave('undo')">
							<svg
								class="brand-connect-cache-control-icon"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								stroke-width="2"
								stroke-linecap="round"
								stroke-linejoin="round"
								aria-hidden="true">
								<path d="M9 14l-4-4 4-4" />
								<path d="M5 10h9a5 5 0 1 1 0 10h-1" />
							</svg>
							<span
								v-if="shouldShowHistoryTooltip('undo')"
								id="brand-connect-cache-tooltip-undo"
								class="brand-connect-assistant-tooltip"
								role="tooltip">
								Undo
							</span>
						</button>

						<button
							class="brand-connect-cache-control-button"
							type="button"
							:disabled="!canHistoryRedo || !!loadingMessage"
							aria-label="Redo"
							aria-describedby="brand-connect-cache-tooltip-redo"
							@click="redoHistory"
							@mouseenter="onHistoryTooltipEnter('redo')"
							@mouseleave="onHistoryTooltipLeave('redo')">
							<svg
								class="brand-connect-cache-control-icon"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								stroke-width="2"
								stroke-linecap="round"
								stroke-linejoin="round"
								aria-hidden="true">
								<path d="M15 14l4-4-4-4" />
								<path d="M19 10H10a5 5 0 1 0 0 10h1" />
							</svg>
							<span
								v-if="shouldShowHistoryTooltip('redo')"
								id="brand-connect-cache-tooltip-redo"
								class="brand-connect-assistant-tooltip"
								role="tooltip">
								Redo
							</span>
						</button>

						<button
							class="brand-connect-cache-control-button"
							type="button"
							:disabled="!!loadingMessage || (!hasContent && cacheHistoryEntries.length === 0)"
							aria-label="Start again"
							aria-describedby="brand-connect-cache-tooltip-start-again"
							@click="startAgainHistory"
							@mouseenter="onHistoryTooltipEnter('start')"
							@mouseleave="onHistoryTooltipLeave('start')">
							<svg
								class="brand-connect-cache-control-icon"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								stroke-width="2"
								stroke-linecap="round"
								stroke-linejoin="round"
								aria-hidden="true">
								<path d="M3 12a9 9 0 1 0 3-6.708" />
								<path d="M3 4v4h4" />
							</svg>
							<span
								v-if="shouldShowHistoryTooltip('start')"
								id="brand-connect-cache-tooltip-start-again"
								class="brand-connect-assistant-tooltip"
								role="tooltip">
								Start again
							</span>
						</button>
					</div>
				</div>
				<div class="brand-connect-brand brand-connect-brand--connector">
					<div class="brand-connect-text" v-if="isRequest">
						<span class="brand-connect-label">From</span>
					</div>
					<div
						class="brand-connect-brand-logo-cell"
						:class="{ 'brand-connect-brand-logo-cell--has-bg-colour': hasBrandLogoBgColour(connectorInfo) }"
						:style="brandLogoCellStyles(connectorInfo)">
						<BrandLogo
							:brandInfo="connectorInfo"
							class="brand-logo connector-logo"
							:uploadAllowed="true"
							:replaceAllowed="!isRequest"
							:showPending="true"
							@reload="getConnectorInfo"
							:leftAligned="!hasBrandLogoBgColour(connectorInfo)"
							:useWhiteBackground="!hasBrandLogoBgColour(connectorInfo)"
							:logoMaxHeight="60" />
					</div>
				</div>
				<div class="brand-connect-brand brand-connect-brand--connectee" v-if="isRequest">
					<div class="brand-connect-text brand-connect-text--right">
						<span class="brand-connect-label">To</span>
					</div>
					<div
						class="brand-connect-brand-logo-cell"
						:class="{ 'brand-connect-brand-logo-cell--has-bg-colour': hasBrandLogoBgColour(connecteeInfo) }"
						:style="brandLogoCellStyles(connecteeInfo)">
						<BrandLogo
							:brandInfo="connecteeInfo"
							class="brand-logo connectee-logo"
							:logoMaxHeight="60"
							:leftAligned="!hasBrandLogoBgColour(connecteeInfo)"
							:useWhiteBackground="!hasBrandLogoBgColour(connecteeInfo)" />
					</div>
				</div>
			</div>
		</template>
		<template v-slot:content>
			<div class="brand-connect-load-error" v-if="!loaded && loadError">
				<p>{{ loadError }}</p>
				<button type="button" class="button mini flip-colours no-margin" @click="getConnectorInfo">Retry</button>
			</div>
			<div :class="{ 'brand-connect-body-loading': true }" v-else-if="!loaded && loadingMessage && loadingMessageSource !== 'brief-writer'">
				<Loading position="inner" class="loading-message" :text="loadingMessage" :animation="loadingAnimation" v-if="loadingMessage" />
			</div>
			<Loading
				position="inner"
				class="loading-message"
				:text="loadingMessage"
				:animation="loadingAnimation"
				v-if="loaded && loadingMessage && loadingMessageSource !== 'brief-writer'" />
			<div :class="{ 'brand-connect-body': true, 'brand-connect-body--request': isRequest }" v-if="loaded">
				<div class="brand-connect-main">
					<div
						v-if="!subscription.featureOnly"
						:class="[
							'brand-connect-assistant-panel',
							'brand-connect-assistant-panel--inline',
							{
								'brand-connect-assistant-panel--collapsed': !isPitchAssistantOpen,
								'brand-connect-assistant-panel--floating': isPitchAssistantOpen,
								'brand-connect-assistant-panel--request': isRequest,
								'brand-connect-assistant-panel--locked': requestAccessMode === 'inactive_anonymous',
								'brand-connect-assistant-panel--showing-locked-notice':
									showWritingToolLockedNotice && requestAccessMode === 'inactive_anonymous',
							},
						]"
						:role="!isPitchAssistantOpen ? 'button' : undefined"
						:tabindex="!isPitchAssistantOpen ? 0 : undefined"
						@click="
							!isPitchAssistantOpen &&
							(requestAccessMode === 'inactive_anonymous' ? handleLockedWritingToolClick() : togglePitchAssistant())
						"
						@keydown="handleAssistantPanelKeydown">
						<Loading
							position="inner"
							class="loading-message brand-connect-assistant-loading-message"
							:text="loadingMessage"
							:animation="loadingAnimation"
							:mobileTextTop="isBelowDesktop"
							v-if="isPitchAssistantOpen && loadingMessage && loadingMessageSource === 'brief-writer'" />
						<div
							v-if="!(showWritingToolLockedNotice && requestAccessMode === 'inactive_anonymous')"
							:class="['brand-connect-assistant-header', { 'brand-connect-assistant-header--collapsed': !isPitchAssistantOpen }]">
							<div class="brand-connect-assistant-try-me" v-if="shouldShowTryMeNudge">
								<img :src="tryMeImage" alt="Try me" />
							</div>
							<div class="brand-connect-assistant-title-row">
								<h3 class="brand-connect-assistant-title">
									<Icon
										type="writing-tool"
										class="brand-connect-assistant-title-icon"
										colour="#679DA2"
										:classes="{ 'writing-tool-animated': !isPitchAssistantOpen }" />
									Writing Tool
								</h3>
								<button
									type="button"
									class="brand-connect-assistant-link"
									@click.stop="toggleWritingToolHints"
									v-if="isPitchAssistantOpen">
									{{ showWritingToolHints ? "hide hints/tips" : "show hints/tips" }}
								</button>
							</div>
							<button
								type="button"
								class="modal-close brand-connect-assistant-close"
								:aria-expanded="isPitchAssistantOpen"
								aria-controls="brand-connect-assistant-body"
								aria-label="Close writing tool"
								@click.stop="togglePitchAssistant"
								v-if="isPitchAssistantOpen" />

							<p
								class="brand-connect-assistant-note"
								v-show="isPitchAssistantOpen && !!writingToolAssistantNote"
								v-html="writingToolAssistantNote" />

							<ul class="brand-connect-assistant-hints-list" v-if="isPitchAssistantOpen && showWritingToolHints">
								<li v-for="hint in writingToolHintItems" :key="hint" v-html="hint" />
							</ul>
						</div>

						<!-- Locked writing tool notice for inactive anonymous users -->
						<div
							class="brand-connect-locked-notice"
							v-if="showWritingToolLockedNotice && requestAccessMode === 'inactive_anonymous'"
							@click.stop>
							<p class="brand-connect-locked-notice-text">
								🔒 <strong>{{ writingToolLockedNoticeMessage }}</strong>
							</p>
							<button
								v-if="showWritingToolLockedUpgradeButton"
								type="button"
								class="button mini flip-colours no-margin"
								@click.stop="openRequestSubscriptionModal">
								Upgrade Now
							</button>
							<button type="button" class="brand-connect-locked-notice-dismiss" @click.stop="showWritingToolLockedNotice = false">
								Dismiss
							</button>
						</div>

						<div id="brand-connect-assistant-body" v-show="isPitchAssistantOpen">
							<div
								id="brand-connect-ai-panel"
								:class="{ 'brand-connect-ai-panel': true, 'brand-connect-ai-panel--loading': loadingMessage }">
								<WritingTool
									ref="writingToolRef"
									:show="showBriefWriter"
									:context="briefWriterContext"
									:inline="true"
									:resumePayload="briefWriterResumePayload"
									@update="handleBriefWriterUpdate"
									@loading-message="(nextLoadingMessage) => updateLoadingMessage(nextLoadingMessage, 'brief-writer')"
									@ui-state="handleBriefWriterUiState"
									@cache-state="handleBriefWriterCacheState" />
							</div>
						</div>
					</div>
					<p class="brand-connect-status-note" v-if="featureAwaitingApproval">
						Your opportunity is awaiting approval. You can still make changes.
					</p>
					<p class="brand-connect-single-source-note" v-if="isSingleSourceEditingActive">
						<Icon
							type="writing-tool"
							class="brand-connect-single-source-note-icon"
							colour="currentColor" />
						<span
							>You are editing through our Writing Tool,
							<span class="clickable-link" @click.stop="togglePitchAssistant">close it</span> to edit the content directly.</span
						>
					</p>
					<template v-if="isRequest">
						<div class="brand-connect-request-dropdown" v-if="!isSingleSourceEditingActive || selectedPartnershipLabels.length > 0">
							<span class="message-label" v-if="!isSingleSourceEditingActive"
								>Choose from partnerships they&apos;re interested in or leave blank to propose something</span
							>
							<span class="message-label" v-else-if="selectedPartnershipLabels.length > 0">Your selected partnership types</span>
							<template v-if="!isSingleSourceEditingActive">
								<v-select
									v-model="partnershipInMind"
									:options="whatPartnershipsDropdown"
									:reduce="(option) => option.id"
									:multiple="true"
									:append-to-body="true"
									:searchable="whatPartnershipsDropdown.length > 20">
									<template #search="{ attributes, events }">
										<input :readonly="isMobile" class="vs__search" v-bind="attributes" v-on="events" />
									</template>
								</v-select>
							</template>
							<p class="brand-connect-readonly-text-list" v-else-if="selectedPartnershipLabels.length > 0">
								{{ selectedPartnershipLabels.join(", ") }}
							</p>
						</div>
						<span class="message-label" v-if="!isSingleSourceEditingActive || requestMessageTextOnlyLabel">
							{{ isSingleSourceEditingActive ? requestMessageTextOnlyLabel : "Now write them a nice message" }}
						</span>
						<div
							:class="{ 'brand-connect-locked-draft-content': showLockedDraftContentBox }"
							v-if="showLockedDraftContentBox || !showLockedDraftEmptyState"
							:role="showLockedDraftContentBox ? 'button' : undefined"
							:tabindex="showLockedDraftContentBox ? 0 : undefined"
							@click.capture="handleLockedDraftEditAttempt"
							@keydown="handleLockedDraftEditAttempt">
							<FormInput
								type="textarea"
								v-model="message"
								name="brand-connect-message"
								theRef="brand-connect-message"
								:typeEffect="true"
								:typeEffectSkipKey="messageTypeEffectSkipKey"
								:textOnly="isSingleSourceEditingActive"
								:textOnlyShowPlaceholder="false"
								:autoResize="true"
								:noMarginBottom="true"
								:required="false"
								:noLabel="true"
								:detectEmails="true"
								@email-found="handleMessageEmailFound"
								:autoResizeMaxHeight="'calc(100vh - 330px)'"
								:placeholder="`Send ${connecteeInfo.brand_name} a message to introduce yourself, your brand and your collaboration idea!`" />
						</div>
						<div class="brand-connect-locked-draft-empty-state" v-if="showLockedDraftEmptyState">
							Your {{ lockedDraftEmptyStateLabel }} will appear here once you&apos;ve asked the Writing Tool to work its magic.
						</div>
						<p class="brand-connect-error" v-if="messageEmailError">{{ messageEmailError }}</p>
					</template>

					<template v-if="isFeature">
						<h3 class="brand-connect-main-title" v-if="!isSingleSourceEditingActive || featureTextOnlyLabel">
							{{ isSingleSourceEditingActive ? featureTextOnlyLabel : "Your opportunity" }}
						</h3>
						<div
							:class="{ 'brand-connect-locked-draft-content': showLockedDraftContentBox }"
							v-if="showLockedDraftContentBox || !showLockedDraftEmptyState"
							:role="showLockedDraftContentBox ? 'button' : undefined"
							:tabindex="showLockedDraftContentBox ? 0 : undefined"
							@click.capture="handleLockedDraftEditAttempt"
							@keydown="handleLockedDraftEditAttempt">
							<FormInput
								type="text"
								v-model="title"
								name="brand-connect-title"
								theRef="brand-connect-title"
								:noLabel="true"
								:required="false"
								:noMarginBottom="true"
								fontSizeInput="20"
								:placeholder="`Add a title e.g Sustainable Christmas Giveaway`"
								:textOnly="isSingleSourceEditingActive"
								:textOnlyShowPlaceholder="isSingleSourceEditingActive"
								:textOnlyPlaceholder="isSingleSourceEditingActive ? 'No title yet' : ''"
								class="feature-title-input" />
							<FormInput
								type="textarea"
								v-model="message"
								name="brand-connect-message"
								theRef="brand-connect-message"
								:typeEffect="true"
								:typeEffectSkipKey="messageTypeEffectSkipKey"
								:textOnly="isSingleSourceEditingActive"
								:textOnlyShowPlaceholder="isSingleSourceEditingActive"
								:textOnlyPlaceholder="isSingleSourceEditingActive ? 'No details yet' : ''"
								:autoResize="true"
								:noMarginBottom="true"
								:required="false"
								:noLabel="true"
								:detectEmails="true"
								@email-found="handleMessageEmailFound"
								:autoResizeMaxHeight="'calc(100vh - 420px)'"
								:placeholder="`Provide details about your opportunity. 

e.g. We are looking for 6 like-minded brands to do '6 days of gifting' in the lead up to Christmas. With a different giveaway and brand every few days starting from 1st December. We have a very engaged audience on social and we will also share across our newsletter for maximum exposure.`" />
							<div class="brand-connect-locked-draft-empty-state" v-if="showLockedDraftEmptyState">
								Your {{ lockedDraftEmptyStateLabel }} will appear here once you&apos;ve asked the Writing Tool to work its magic.
							</div>
							<p class="brand-connect-error" v-if="messageEmailError">{{ messageEmailError }}</p>

							<div
								:class="[
									'brand-connect-aside-panel',
									'brand-connect-aside-section',
									'brand-connect-aside-section--requirements',
									{ 'brand-connect-aside-panel--embedded': isSingleSourceEditingActive && showLockedDraftContentBox },
								]">
								<div class="requirements-panel">
									<div class="requirements-grid">
										<div class="requirements-section">
											<h4 class="section-label wants" v-if="!isSingleSourceEditingActive">
												What you&apos;re looking for in a brand (5 max):
											</h4>
											<template v-if="!isSingleSourceEditingActive">
												<div class="input-with-button">
													<FormInput
														type="text"
														v-model="brandRequirements.want"
														placeholder="e.g. Sustainable brands"
														:inline="true"
														:required="false"
														:noMarginBottom="true"
														@aKeyup-enter="addItemToList('want')"
														:disabled="brandRequirements.wants.length >= 5" />
													<button
														v-if="brandRequirements.want && brandRequirements.wants.length < 5"
														class="add-item-button"
														@click="addItemToList('want')"
														type="button"
														title="Add item">
														<svg
															class="plus-icon"
															xmlns="http://www.w3.org/2000/svg"
															viewBox="0 0 24 24"
															fill="currentColor">
															<path d="M11 11V5h2v6h6v2h-6v6h-2v-6H5v-2h6z" />
														</svg>
													</button>
												</div>
												<RequirementsList
													:items="brandRequirements.wants"
													:editing="true"
													@delete="deleteItem"
													@update:items="updateWants" />
												<p class="hint-text" v-if="brandRequirements.wants.length === 0">Hit enter/return after each item.</p>
											</template>
											<RequirementsList
												v-else-if="brandRequirements.wants.length > 0"
												:items="brandRequirements.wants"
												:editing="false" />
										</div>

										<div class="requirements-section">
											<h4 class="section-label bads" v-if="!isSingleSourceEditingActive">
												What you&apos;re not looking for in a brand (5 max):
											</h4>
											<template v-if="!isSingleSourceEditingActive">
												<div class="input-with-button">
													<FormInput
														type="text"
														v-model="brandRequirements.bad"
														placeholder="e.g. App brands"
														:inline="true"
														:required="false"
														:noMarginBottom="true"
														@aKeyup-enter="addItemToList('bad')"
														:disabled="brandRequirements.bads.length >= 5" />
													<button
														v-if="brandRequirements.bad && brandRequirements.bads.length < 5"
														class="add-item-button"
														@click="addItemToList('bad')"
														type="button"
														title="Add item">
														<svg
															class="plus-icon"
															xmlns="http://www.w3.org/2000/svg"
															viewBox="0 0 24 24"
															fill="currentColor">
															<path d="M11 11V5h2v6h6v2h-6v6h-2v-6H5v-2h6z" />
														</svg>
													</button>
												</div>
												<RequirementsList
													:items="brandRequirements.bads"
													:positive="false"
													:editing="true"
													@delete="deleteItem"
													@update:items="updateBads" />
												<p class="hint-text mobile-only" v-if="brandRequirements.bads.length === 0">
													Hit enter/return after each item.
												</p>
											</template>
											<RequirementsList
												v-else-if="brandRequirements.bads.length > 0"
												:items="brandRequirements.bads"
												:positive="false"
												:editing="false" />
										</div>
									</div>
								</div>
							</div>
						</div>
					</template>

					<div class="lifestyle-image-section" v-if="isFeature">
						<div class="image-upload-wrap">
							<span
								class="button flip-colours mini no-margin no-case"
								@click="uploadImage('pitch-lifestyle')"
								v-html="!!lifestyleImage ? 'Update lifestyle image' : 'Upload lifestyle image'" />
							<p>Please upload an image that is at least 1200 pixels wide by 600 pixels high.</p>
							<img :src="lifestyleImage" alt="" v-if="lifestyleImage" />
						</div>
					</div>
					<p class="connect-message send-request brown" v-if="isRequest && activeBrand && requestAccessMode === 'active'">
						<strong>{{ activeBrand.brand_name }}</strong> will be shared your details and if it's the right fit, they will connect with you.
						<br />
						<br />
						⏱️ <strong>Responses may take longer during busy periods.</strong>
					</p>
				</div>
			</div>
		</template>
		<template v-slot:footer-inner>
			<p class="warning-message" v-if="isFeature && !subscription.active && !subscription.featureOnly">
				Your opportunity will not be visible to brands until you upgrade.
			</p>
			<div class="footer-wrap">
				<p class="connect-message send-request brown" v-if="isRequest && activeBrand && requestAccessMode === 'active'">
					<strong>{{ activeBrand.brand_name }}</strong> will be shared your details and if it's the right fit, they will connect with you.
					<br />
					<br />
					⏱️ <strong>Responses may take longer during busy periods.</strong>
				</p>
				<p class="connect-message brown contact-details" v-if="isFeature && customContactMessage">
					{{ customContactMessage }}, <span class="clickable-link" @click="showContactPopup = true">update here</span>.
				</p>

				<!-- Notice for inactive users with visible brand name -->
				<p class="connect-message brown" v-if="isRequest && requestAccessMode === 'inactive_visible'">
					💡 <strong>Draft auto saved:</strong> You can create this request and use the Writing Tool, but you'll need to upgrade to send it.
				</p>

				<!-- Notice for inactive users with anonymous brand -->
				<p class="connect-message brown" v-if="isRequest && requestAccessMode === 'inactive_anonymous' && subscription.approved">
					🔒 <strong>Limited preview:</strong> This brand is hidden on your current plan. You can draft a basic request and we'll save it
					here for later, but you'll need to upgrade to use the Writing Tool and/or send it.
				</p>

				<p class="connect-message brown" v-if="isRequest && requestAccessMode === 'inactive_anonymous' && !subscription.approved">
					🔒 <strong>Limited preview:</strong> Your account is awaiting approval by the team.
				</p>

				<div class="button-wrap" v-if="subscription.approved || isFeature">
					<span class="button hollow no-case no-margin" @click="close()">Cancel</span>
					<span
						v-if="!isRequest || requestAccessMode === 'active'"
						class="button flip-colours no-case no-margin"
						@click="isFeature ? previewFeature() : close(true)"
						v-html="isFeature ? (featureAwaitingApproval ? 'Preview' : 'Preview / Submit') : 'Send Request'"></span>
					<span
						v-else-if="isRequest && (requestAccessMode === 'inactive_visible' || requestAccessMode === 'inactive_anonymous')"
						class="button flip-colours no-case no-margin"
						@click="openRequestSubscriptionModal">
						Send Request (Upgrade needed)
					</span>
				</div>
			</div>
		</template>
	</Pupup>

	<!-- Contact Details Popup -->
	<Pupup v-if="showContactPopup" @close="showContactPopup = false" :showClose="true" :width="500" :clickOverlayToClose="true" :shuffleUp="true">
		<template v-slot:header>
			<h3 class="contact-popup-title">Where Pitches Go...</h3>
		</template>
		<template v-slot:content>
			<div class="contact-popup-content">
				<p>If you'd like pitches sent to a different person, add their details below.</p>
				<div class="input-wrap-wrap">
					<div class="input-wrap">
						<FormInput
							type="text"
							v-model="customContact.fname"
							placeholder=""
							label="First name"
							:required="false"
							:noMarginBottom="true"
							requiredText="*" />
					</div>

					<div class="input-wrap">
						<FormInput
							type="text"
							v-model="customContact.sname"
							placeholder=""
							label="Surname"
							:required="false"
							:noMarginBottom="true"
							requiredText="*" />
					</div>
				</div>

				<div class="input-wrap">
					<FormInput
						type="email"
						v-model="customContact.email"
						placeholder=""
						label="Email"
						:required="false"
						:noMarginBottom="true"
						requiredText="*" />
				</div>
			</div>
		</template>
		<template v-slot:footer-inner>
			<div class="contact-popup-footer">
				<span class="button flip-colours no-case no-margin" @click="saveCustomContact">Save Details</span>
			</div>
		</template>
	</Pupup>

	<!-- Uploader Component -->
	<Uploader
		:imagesOnly="true"
		:title="uploaderTitle"
		:dragText="uploaderDragText"
		:formData="uploaderFormData"
		@close-uploader="closeUploader"
		v-if="showUploader" />
</template>

<script>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from "vue";

const SINGLE_SOUCE_EDITING = true;
import { useVueInstance, useDialogs } from "../../composables/useVueInstance";
import { TokenService } from "../../services/storage.service";
import Pupup from "./Pupup.vue";
import BrandLogo from "./BrandLogo.vue";
import ApiService from "./../../services/api.service";
import {
	buildDedupedBrandConnectHistoryEntries,
	hasMeaningfulBrandConnectFeatureSnapshot,
	isSnapshotEquivalent,
} from "../../utils/brandConnectCacheHistory.js";
import FormInput from "./../../components/Form/Input.vue";
import RequirementsList from "./RequirementsList.vue";
import Uploader from "./../Uploader.vue";
import WritingTool from "./../WritingTool.vue";
import LoadingSpinner from "./../LoadingSpinner.vue";
import Icon from "./BaseIcon.vue";
import tryMeImage from "./../../assets/img/try-me.svg";
export default {
	name: "BrandConnect",
	emits: ["close", "preview-feature"],
	components: {
		Pupup,
		BrandLogo,
		FormInput,
		RequirementsList,
		Uploader,
		WritingTool,
		Loading: LoadingSpinner,
		Icon,
	},
	props: {
		connecteeId: {
			type: Number,
			required: false,
		},
		initialData: {
			type: Object,
			required: false,
			default: null,
		},
	},
	setup(props, { emit }) {
		const { store, $toast } = useVueInstance();
		const { confirm } = useDialogs();
		const subscription = computed(() => store.state.general.subscription || {});
		const brandConnectModal = computed(() => store.state.general.brandConnectModal || {});
		const resolvedConnecteeId = computed(() =>
			typeof props.connecteeId === "undefined" || props.connecteeId === null ? brandConnectModal.value.connecteeId : props.connecteeId
		);
		const resolvedInitialData = computed(() =>
			typeof props.initialData === "undefined" || props.initialData === null ? brandConnectModal.value.initialData : props.initialData
		);
		const normalizedFeatureInitialData = computed(() => {
			const data = resolvedInitialData.value || {};
			return {
				...data,
				pending:
					typeof data.pending === "boolean"
						? data.pending
						: typeof data.pendingApproval === "boolean"
							? data.pendingApproval
							: data.approved === 0,
				paid: typeof data.paid === "boolean" ? data.paid : typeof data.requiresPayment === "boolean" ? !data.requiresPayment : false,
				current:
					typeof data.current === "boolean"
						? data.current
						: !!(data.expires && new Date(data.expires).getTime() > Date.now() && data.approved < 2),
				_id: data._id || data.id || null,
				pitch_id: data.pitch_id || data.pitchId || data._id || data.id || null,
			};
		});
		const initialConnecteePreview = () => {
			const preview = resolvedInitialData.value?.requestMeta?.connecteePreview;
			return preview && typeof preview === "object" ? preview : {};
		};
		const connectorInfo = ref({});
		const connecteeInfo = ref(initialConnecteePreview());
		const loaded = ref(false);
		const loadingMessage = ref("Loading brand details…");
		const loadingMessageSource = ref("system");
		const loadingAnimation = ref("");
		const scrollTrigger = ref(0);
		const message = ref("");
		const title = ref("");
		const featurePitchId = ref(null);
		const messageEmailError = ref("");
		const messageEmailPattern = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
		const requestValidationMessage = "Please write the brand a short message before sending your request.";

		const isRequest = computed(() => !!resolvedConnecteeId.value);
		const isFeature = computed(() => !isRequest.value);
		const featureAwaitingApproval = computed(
			() => isFeature.value && !!normalizedFeatureInitialData.value.pending && !normalizedFeatureInitialData.value.approved
		);

		// Request access mode for progressive gating
		const isInactiveRequestUser = computed(() => isRequest.value && !subscription.value.active);
		const brandNameVisible = computed(() => {
			// Priority: loaded connecteeInfo, then requestMeta from initialData, fallback false
			if (connecteeInfo.value && typeof connecteeInfo.value.show_brand_name !== "undefined") {
				return !!connecteeInfo.value.show_brand_name;
			}
			if (resolvedInitialData.value?.requestMeta?.brandNameVisible !== undefined) {
				return !!resolvedInitialData.value.requestMeta.brandNameVisible;
			}
			return false;
		});

		const requestAccessMode = computed(() => {
			if (!isRequest.value) return null;
			if (subscription.value.active) return "active";
			if (brandNameVisible.value) return "inactive_visible";
			return "inactive_anonymous";
		});

		const writingToolLockedNoticeMessage = computed(() => {
			if (!subscription.value.approved) {
				return "You can't use the Writing Tool whilst your account is awaiting approval by the team.";
			}

			return "Upgrade to reveal this brand and use the Writing Tool.";
		});

		const showWritingToolLockedUpgradeButton = computed(() => !!subscription.value.approved);
		const normalizedLogoBgColour = (brandInfo) => {
			const value = brandInfo?.brand_logo_bg_colour;
			if (typeof value !== "string") return "";
			const colour = value.trim();
			return /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(colour) ? colour : "";
		};
		const hasBrandLogoBgColour = (brandInfo) => !!normalizedLogoBgColour(brandInfo);
		const brandLogoCellStyles = (brandInfo) => {
			const colour = normalizedLogoBgColour(brandInfo);
			return colour ? { backgroundColor: colour } : {};
		};

		// UI state for locked features
		const showWritingToolLockedNotice = ref(false);
		const briefWriterRequestSeq = ref(0);

		const partnershipInMind = ref([]);
		const loadError = ref("");
		const pendingSubscriptionRefresh = ref(false);

		const showBriefWriter = ref(true);
		const isPitchAssistantOpen = ref(false);
		const hasOpenedWritingTool = computed(() => !!store.state.general.hasOpenedWritingTool);
		const briefWriterContext = ref(null);
		const briefWriterResumePayload = ref(null);
		const showWritingToolHints = ref(false);
		const briefWriterCacheState = ref({
			open: false,
			runId: null,
			type: null,
			cursor: null,
			refineNotesDraft: "",
			updatedAt: null,
		});

		const writingToolRef = ref(null);
		const briefWriterUiState = ref({
			hasTimeline: false,
			isBaselineCursor: false,
			runId: null,
			canUndo: false,
			canRedo: false,
		});
		const suppressAutoFocus = ref(false);
		const allowBriefWriterAutoScroll = ref(false);

		const CACHE_AUTOSAVE_MS = 10000;
		const cacheLoaded = ref(false);
		const cacheExists = ref(false);
		const cacheDirty = ref(false);
		const cacheSaving = ref(false);
		const cacheSavedAt = ref(null);
		const cacheHasSavedContent = ref(false);
		const suppressCacheDirty = ref(false);
		const cacheHistoryEntries = ref([]);
		const cacheHistoryCursor = ref(-1);
		const cacheHistoryLoaded = ref(false);
		const immediateSaveTimer = ref(null);
		const isApplyingHistory = ref(false);
		const messageTypeEffectSkipKey = ref(0);
		const suppressAutoSaveUntil = ref(0);
		const initialFeatureDraftApplied = ref(false);

		const cacheEnabled = computed(() => loaded.value);

		const cacheBadgeText = computed(() => {
			if (!cacheEnabled.value) return "";
			if (cacheSaving.value) return "Saving...";
			if (cacheExists.value && !cacheDirty.value && cacheHasSavedContent.value) return "Saved";
			return "";
		});

		const cacheBadgeHasTooltip = computed(() => cacheBadgeText.value === "Saved");
		const cacheBadgeTooltipId = "brand-connect-cache-badge-tooltip";
		const cacheBadgeTooltipReady = ref(true);
		const cacheBadgeHover = ref(false);
		const showCacheStatusUi = false;
		const activeHistoryTooltip = ref(null);
		const persistedFeatureData = ref(null);

		const normaliseIdList = (value) => {
			if (!Array.isArray(value)) return [];
			return value.map((v) => (typeof v === "number" ? v : parseInt(v, 10))).filter((v) => !!v && !isNaN(v));
		};

		const buildPersistedFeatureData = (value = resolvedInitialData.value) => {
			const source = value && typeof value === "object" ? value : {};
			const data =
				source.persistedData && typeof source.persistedData === "object"
					? source.persistedData
					: source.originalData && typeof source.originalData === "object"
						? source.originalData
						: source;
			const wants = Array.isArray(data.wants) ? data.wants : Array.isArray(data.lists?.wants) ? data.lists.wants : [];
			const bads = Array.isArray(data.bads) ? data.bads : Array.isArray(data.lists?.bads) ? data.lists.bads : [];
			const lifestyleImageValue =
				typeof data.lifestyleImage === "string"
					? data.lifestyleImage
					: typeof data["pitch-lifestyle"] === "string"
						? data["pitch-lifestyle"]
						: "";
			const customContactValue =
				data.customContact && typeof data.customContact === "object"
					? data.customContact
					: data.account_contact === false && typeof data.email === "string" && data.email.trim()
						? {
								fname: typeof data.firstName === "string" ? data.firstName : "",
								sname: typeof data.surname === "string" ? data.surname : "",
								email: data.email,
							}
						: null;

			return {
				title: typeof data.title === "string" ? data.title : typeof data.pitch_title === "string" ? data.pitch_title : "",
				message: typeof data.message === "string" ? data.message : typeof data.pitch_description === "string" ? data.pitch_description : "",
				wants: [...wants],
				bads: [...bads],
				lifestyleImage: lifestyleImageValue,
				customContact:
					customContactValue && typeof customContactValue.email === "string" && customContactValue.email.trim()
						? {
								fname:
									typeof customContactValue.fname === "string"
										? customContactValue.fname
										: typeof customContactValue.firstName === "string"
											? customContactValue.firstName
											: "",
								sname:
									typeof customContactValue.sname === "string"
										? customContactValue.sname
										: typeof customContactValue.surname === "string"
											? customContactValue.surname
											: "",
								email: typeof customContactValue.email === "string" ? customContactValue.email : "",
							}
						: null,
			};
		};

		const featureInitialDataKey = computed(() => {
			if (!isFeature.value || !resolvedInitialData.value) return "";
			const data = normalizedFeatureInitialData.value;
			return JSON.stringify({
				pitchId: data.pitch_id || null,
				title: data.pitch_title || "",
				message: data.pitch_description || "",
				wants: Array.isArray(data.lists?.wants) ? data.lists.wants : [],
				bads: Array.isArray(data.lists?.bads) ? data.lists.bads : [],
				lifestyleImage: data["pitch-lifestyle"] || "",
				accountContact: data.account_contact,
				firstName: data.firstName || "",
				surname: data.surname || "",
				email: data.email || "",
			});
		});

		const applyInitialFeatureDraft = () => {
			if (!resolvedInitialData.value || !isFeature.value || initialFeatureDraftApplied.value) return;

			title.value = resolvedInitialData.value.pitch_title || "";
			message.value = resolvedInitialData.value.pitch_description || "";
			brandRequirements.value.wants = Array.isArray(resolvedInitialData.value.lists?.wants) ? [...resolvedInitialData.value.lists.wants] : [];
			brandRequirements.value.bads = Array.isArray(resolvedInitialData.value.lists?.bads) ? [...resolvedInitialData.value.lists.bads] : [];
			lifestyleImage.value = resolvedInitialData.value["pitch-lifestyle"] || "";

			const pitchIdRaw =
				normalizedFeatureInitialData.value.pitch_id != null
					? normalizedFeatureInitialData.value.pitch_id
					: normalizedFeatureInitialData.value.pitchId != null
						? normalizedFeatureInitialData.value.pitchId
						: null;
			const pitchIdParsed = pitchIdRaw != null ? parseInt(pitchIdRaw, 10) : null;
			featurePitchId.value = Number.isFinite(pitchIdParsed) ? pitchIdParsed : null;

			customContact.value = {
				fname: "",
				sname: "",
				email: "",
			};
			if (!resolvedInitialData.value.account_contact && resolvedInitialData.value.firstName) {
				customContact.value.fname = resolvedInitialData.value.firstName;
				customContact.value.sname = resolvedInitialData.value.surname;
				customContact.value.email = resolvedInitialData.value.email;
			}

			initialFeatureDraftApplied.value = true;
		};

		const buildSnapshot = () => {
			const bw = briefWriterCacheState.value && typeof briefWriterCacheState.value === "object" ? briefWriterCacheState.value : {};
			const cursor = bw.cursor && typeof bw.cursor === "object" ? bw.cursor : null;

			const initialPitchIdRaw =
				resolvedInitialData.value && resolvedInitialData.value.pitch_id != null
					? parseInt(resolvedInitialData.value.pitch_id, 10)
					: resolvedInitialData.value && resolvedInitialData.value.pitchId != null
						? parseInt(resolvedInitialData.value.pitchId, 10)
						: null;

			const initialPitchId = Number.isFinite(initialPitchIdRaw) ? initialPitchIdRaw : null;

			return {
				version: 3,
				savedAt: new Date().toISOString(),
				data: {
					message: message.value || "",
					title: title.value || "",
					pitchId: isFeature.value ? (featurePitchId.value != null ? featurePitchId.value : initialPitchId) : undefined,
					partnerships: Array.isArray(partnershipInMind.value) ? partnershipInMind.value : [],
					brandRequirements: {
						want: brandRequirements.value.want || "",
						bad: brandRequirements.value.bad || "",
						wants: Array.isArray(brandRequirements.value.wants) ? brandRequirements.value.wants : [],
						bads: Array.isArray(brandRequirements.value.bads) ? brandRequirements.value.bads : [],
					},
					lifestyleImage: lifestyleImage.value || "",
					customContact: {
						fname: customContact.value.fname || "",
						sname: customContact.value.sname || "",
						email: customContact.value.email || "",
					},
				},
				briefWriter: {
					open: !!bw.open,
					runId: bw.runId != null ? bw.runId : null,
					type: bw.type === "feature" ? "feature" : bw.type === "request" ? "request" : isFeature.value ? "feature" : "request",
					cursor,
					refineNotesDraft: typeof bw.refineNotesDraft === "string" ? bw.refineNotesDraft : "",
					updatedAt: bw.updatedAt || null,
				},
			};
		};

		const cloneSnapshot = (snapshot) => {
			try {
				return JSON.parse(JSON.stringify(snapshot));
			} catch (e) {
				return snapshot;
			}
		};

		const applySnapshot = async (snapshot) => {
			const s = snapshot && typeof snapshot === "object" ? snapshot : {};
			const version = s.version === 3 ? 3 : s.version === 2 ? 2 : 1;
			if (version !== 1 && version !== 2 && version !== 3) return;

			const d = s.data && typeof s.data === "object" ? s.data : {};
			const br = d.brandRequirements && typeof d.brandRequirements === "object" ? d.brandRequirements : {};
			const cc = d.customContact && typeof d.customContact === "object" ? d.customContact : {};

			suppressCacheDirty.value = true;

			message.value = typeof d.message === "string" ? d.message : "";
			title.value = typeof d.title === "string" ? d.title : "";
			partnershipInMind.value = normaliseIdList(d.partnerships);

			if (isFeature.value) {
				const pitchIdRaw =
					d.pitchId != null
						? d.pitchId
						: d.pitch_id != null
							? d.pitch_id
							: resolvedInitialData.value && resolvedInitialData.value.pitch_id != null
								? resolvedInitialData.value.pitch_id
								: resolvedInitialData.value && resolvedInitialData.value.pitchId != null
									? resolvedInitialData.value.pitchId
									: null;

				const pitchIdParsed = pitchIdRaw != null ? parseInt(pitchIdRaw, 10) : null;
				featurePitchId.value = Number.isFinite(pitchIdParsed) ? pitchIdParsed : null;
			}

			brandRequirements.value.want = typeof br.want === "string" ? br.want : "";
			brandRequirements.value.bad = typeof br.bad === "string" ? br.bad : "";
			brandRequirements.value.wants = Array.isArray(br.wants) ? br.wants.slice(0, 5) : [];
			brandRequirements.value.bads = Array.isArray(br.bads) ? br.bads.slice(0, 5) : [];

			lifestyleImage.value = typeof d.lifestyleImage === "string" ? d.lifestyleImage : "";

			customContact.value = {
				fname: typeof cc.fname === "string" ? cc.fname : "",
				sname: typeof cc.sname === "string" ? cc.sname : "",
				email: typeof cc.email === "string" ? cc.email : "",
			};

			if (version === 2 || version === 3) {
				const bw = s.briefWriter && typeof s.briefWriter === "object" ? s.briefWriter : {};

				const runIdRaw = bw.runId != null ? parseInt(bw.runId, 10) : null;
				const runId = Number.isFinite(runIdRaw) ? runIdRaw : null;

				const cursor =
					bw.cursor && typeof bw.cursor === "object"
						? {
								kind: bw.cursor.kind === "iteration" ? "iteration" : bw.cursor.kind === "base" ? "base" : "baseline",
								iterationIndex:
									bw.cursor.iterationIndex === 0 || bw.cursor.iterationIndex === "0"
										? 0
										: bw.cursor.iterationIndex != null
											? parseInt(bw.cursor.iterationIndex, 10)
											: null,
							}
						: null;

				briefWriterCacheState.value = {
					open: bw.open === true,
					runId,
					type: bw.type === "feature" ? "feature" : bw.type === "request" ? "request" : null,
					cursor,
					refineNotesDraft: typeof bw.refineNotesDraft === "string" ? bw.refineNotesDraft : "",
					updatedAt: typeof bw.updatedAt === "string" ? bw.updatedAt : null,
				};
				//isPitchAssistantOpen.value = bw.open === true;
			} else {
				briefWriterCacheState.value = {
					open: false,
					runId: null,
					type: null,
					cursor: null,
					refineNotesDraft: "",
					updatedAt: null,
				};
				isPitchAssistantOpen.value = false;
			}

			briefWriterResumePayload.value = null;

			await nextTick();

			suppressCacheDirty.value = false;
		};

		const applyHistoryEntry = async (entry) => {
			if (!entry || !entry.snapshot) return;
			isApplyingHistory.value = true;
			messageTypeEffectSkipKey.value += 1;
			suppressCacheDirty.value = true;
			suppressAutoSaveUntil.value = Date.now() + 2000;
			await applySnapshot(entry.snapshot);
			refreshBriefWriterContext();
			await restoreBriefWriterFromCache(entry.snapshot);
			const bw = briefWriterCacheState.value && typeof briefWriterCacheState.value === "object" ? briefWriterCacheState.value : {};
			if (!bw.runId) {
				const instance = writingToolRef.value;
				if (instance && typeof instance.clearTimeline === "function") {
					await instance.clearTimeline();
				}
			}
			cacheDirty.value = false;
			cacheExists.value = true;
			cacheSavedAt.value = entry.createdAt || null;
			cacheHasSavedContent.value = !!(entry.snapshot && hasMeaningfulBrandConnectFeatureSnapshot(entry.snapshot));
			await nextTick();
			suppressCacheDirty.value = false;
			isApplyingHistory.value = false;
		};

		function findPrevDistinctIndex(startIndex, entries) {
			if (!Array.isArray(entries) || !entries.length) return null;
			if (startIndex == null || startIndex <= 0) return null;
			const current = entries[startIndex] || null;
			for (let i = startIndex - 1; i >= 0; i -= 1) {
				if (!isSnapshotEquivalent(entries[i]?.snapshot, current?.snapshot)) {
					return i;
				}
			}
			return null;
		}

		function findNextDistinctIndex(startIndex, entries) {
			if (!Array.isArray(entries) || !entries.length) return null;
			if (startIndex == null || startIndex >= entries.length - 1) return null;
			const current = entries[startIndex] || null;
			for (let i = startIndex + 1; i < entries.length; i += 1) {
				if (!isSnapshotEquivalent(entries[i]?.snapshot, current?.snapshot)) {
					return i;
				}
			}
			return null;
		}

		const canHistoryUndo = computed(() => findPrevDistinctIndex(cacheHistoryCursor.value, cacheHistoryEntries.value) !== null);
		const canHistoryRedo = computed(() => findNextDistinctIndex(cacheHistoryCursor.value, cacheHistoryEntries.value) !== null);

		const setHistoryCursor = async (nextCursor) => {
			if (!Array.isArray(cacheHistoryEntries.value) || !cacheHistoryEntries.value.length) return;
			const safeCursor = Math.min(Math.max(nextCursor, 0), cacheHistoryEntries.value.length - 1);
			const entry = cacheHistoryEntries.value[safeCursor] || null;
			cacheHistoryCursor.value = safeCursor;
			await applyHistoryEntry(entry);
		};

		const loadBrandConnectHistory = async () => {
			if (!cacheEnabled.value) {
				cacheHistoryLoaded.value = true;
				return;
			}

			const type = isRequest.value ? "request" : "feature";
			const params = { type, limit: 50 };
			if (type === "request") {
				params.recipientBrandId = resolvedConnecteeId.value;
			}

			try {
				const response = await ApiService.get("brand-connect/cache/history", { params });
				const data = response && response.data ? response.data : {};
				const entries = Array.isArray(data.entries) ? data.entries : [];
				const deduped = buildDedupedBrandConnectHistoryEntries(entries);

				cacheHistoryEntries.value = deduped;
				cacheHistoryCursor.value = cacheHistoryEntries.value.length ? cacheHistoryEntries.value.length - 1 : -1;

				if (cacheHistoryCursor.value >= 0) {
					await applyHistoryEntry(cacheHistoryEntries.value[cacheHistoryCursor.value]);
				}

				cacheExists.value = cacheHistoryEntries.value.length > 0;
			} catch (err) {
				console.error("BrandConnect.cache.history error:", err);
			} finally {
				cacheHistoryLoaded.value = true;
			}
		};

		const loadBrandConnectCache = async () => {
			if (!cacheEnabled.value) {
				cacheLoaded.value = true;
				return;
			}

			if (cacheHistoryLoaded.value && cacheHistoryEntries.value.length) {
				cacheLoaded.value = true;
				cacheExists.value = true;
				return;
			}

			const type = isRequest.value ? "request" : "feature";
			const params = { type };
			if (type === "request") {
				params.recipientBrandId = resolvedConnecteeId.value;
			}

			try {
				const response = await ApiService.get("brand-connect/cache", { params });
				const data = response && response.data ? response.data : {};

				if (data && data.exists) {
					cacheExists.value = true;
					cacheDirty.value = false;
					cacheSavedAt.value = data.updatedAt || null;
					cacheHasSavedContent.value = !!(data.snapshot && hasMeaningfulBrandConnectFeatureSnapshot(data.snapshot));

					// Apply snapshot consistently on modal open when it exists.
					if (data.snapshot) {
						await applySnapshot(data.snapshot);
					}
				} else {
					cacheExists.value = false;
					cacheHasSavedContent.value = false;
				}
			} catch (err) {
				console.error("BrandConnect.cache.get error:", err);
			} finally {
				cacheLoaded.value = true;
			}
		};

		const appendHistoryEntry = async (reason = "autosave", options = {}) => {
			if (!cacheEnabled.value) return;
			if (!cacheLoaded.value && !cacheHistoryLoaded.value) return;
			if (cacheSaving.value) return;
			if (!options.force && !cacheDirty.value) return;

			const snapshot = cloneSnapshot(buildSnapshot());
			const hasContent = hasMeaningfulBrandConnectFeatureSnapshot(snapshot);

			// Avoid creating rows for totally empty drafts unless forced.
			if (!hasContent && !cacheExists.value && !options.force) {
				cacheDirty.value = false;
				cacheHasSavedContent.value = false;
				return;
			}

			const last = cacheHistoryEntries.value[cacheHistoryEntries.value.length - 1];
			if (last && isSnapshotEquivalent(last.snapshot, snapshot)) {
				// Treat as saved (but don't spam history with timestamp-only deltas).
				cacheExists.value = true;
				cacheDirty.value = false;
				cacheSavedAt.value = last.createdAt || cacheSavedAt.value;
				cacheHasSavedContent.value = hasContent;
				return;
			}

			const type = isRequest.value ? "request" : "feature";
			const body = { type, snapshot, reason };

			if (type === "request") {
				body.recipientBrandId = resolvedConnecteeId.value;
			}

			cacheSaving.value = true;

			try {
				const response = await ApiService.post("brand-connect/cache/history", body);
				const data = response && response.data ? response.data : {};
				const createdAt = data.createdAt || new Date().toISOString();

				const last = cacheHistoryEntries.value[cacheHistoryEntries.value.length - 1];
				if (!last || !isSnapshotEquivalent(last.snapshot, snapshot)) {
					cacheHistoryEntries.value.push({
						id: data.id || null,
						createdAt,
						snapshot,
					});
					cacheHistoryCursor.value = cacheHistoryEntries.value.length - 1;
				}

				cacheExists.value = true;
				cacheDirty.value = false;
				cacheSavedAt.value = createdAt;
				cacheHasSavedContent.value = hasContent;
			} catch (err) {
				console.error("BrandConnect.cache.history.post error:", err);
			} finally {
				cacheSaving.value = false;
			}
		};

		const saveBrandConnectCache = async (options = {}) => {
			await appendHistoryEntry(options.reason || "autosave", options);
		};

		const saveLatestBrandConnectCache = async () => {
			if (!cacheEnabled.value) return;
			if (!cacheLoaded.value && !cacheHistoryLoaded.value) return;

			const snapshot = cloneSnapshot(buildSnapshot());
			const hasContent = hasMeaningfulBrandConnectFeatureSnapshot(snapshot);
			const type = isRequest.value ? "request" : "feature";
			const body = { type, snapshot };

			if (type === "request") {
				body.recipientBrandId = resolvedConnecteeId.value;
			}

			try {
				const response = await ApiService.put("brand-connect/cache", body);
				const data = response && response.data ? response.data : {};
				cacheExists.value = true;
				cacheDirty.value = false;
				cacheSavedAt.value = data.updatedAt || new Date().toISOString();
				cacheHasSavedContent.value = hasContent;
			} catch (err) {
				console.error("BrandConnect.cache.post error:", err);
			}
		};

		const markCacheDirty = () => {
			if (!cacheEnabled.value) return;
			if (!cacheLoaded.value && !cacheHistoryLoaded.value) return;
			if (suppressCacheDirty.value || isApplyingHistory.value || Date.now() < suppressAutoSaveUntil.value) return;
			cacheDirty.value = true;
		};

		const triggerImmediateSave = (reason = "instant") => {
			if (!cacheEnabled.value) return;
			if (!cacheLoaded.value && !cacheHistoryLoaded.value) return;
			if (suppressCacheDirty.value || isApplyingHistory.value || Date.now() < suppressAutoSaveUntil.value) return;

			if (immediateSaveTimer.value) {
				clearTimeout(immediateSaveTimer.value);
			}

			immediateSaveTimer.value = setTimeout(() => {
				saveBrandConnectCache({ reason, force: true });
				immediateSaveTimer.value = null;
			}, 200);
		};

		const armCacheBadgeTooltip = () => {
			cacheBadgeHover.value = false;
			if (cacheBadgeText.value !== "Saved") return;
			cacheBadgeTooltipReady.value = true;
		};

		const onCacheBadgeEnter = () => {
			cacheBadgeHover.value = true;
		};

		const onHistoryTooltipEnter = (key) => {
			activeHistoryTooltip.value = key;
		};

		const onHistoryTooltipLeave = (key) => {
			if (activeHistoryTooltip.value !== key) return;
			activeHistoryTooltip.value = null;
		};

		const shouldShowHistoryTooltip = (key) => activeHistoryTooltip.value === key;

		const restoreBriefWriterFromCache = async (snapshotOverride = null) => {
			if (!cacheEnabled.value) return;

			// Skip WritingTool restore for locked mode (inactive anonymous users)
			if (requestAccessMode.value === "inactive_anonymous") return;

			suppressAutoSaveUntil.value = Date.now() + 2000;

			const bw = briefWriterCacheState.value && typeof briefWriterCacheState.value === "object" ? briefWriterCacheState.value : {};

			const cachedRunId = bw.runId != null ? parseInt(bw.runId, 10) : null;
			if (!cachedRunId || isNaN(cachedRunId)) return;

			const snapshot = snapshotOverride && typeof snapshotOverride === "object" ? snapshotOverride : null;
			const snapshotData = snapshot && snapshot.data && typeof snapshot.data === "object" ? snapshot.data : null;
			const snapshotRequirements =
				snapshotData && snapshotData.brandRequirements && typeof snapshotData.brandRequirements === "object"
					? snapshotData.brandRequirements
					: null;

			const messageDraftValue = snapshotData && typeof snapshotData.message === "string" ? snapshotData.message : message.value || "";

			const titleDraftValue = snapshotData && typeof snapshotData.title === "string" ? snapshotData.title : title.value || "";

			const partnershipsDraftValue =
				snapshotData && Array.isArray(snapshotData.partnerships)
					? snapshotData.partnerships
					: Array.isArray(partnershipInMind.value)
						? partnershipInMind.value
						: [];

			const wantsDraftValue =
				snapshotRequirements && Array.isArray(snapshotRequirements.wants)
					? snapshotRequirements.wants
					: Array.isArray(brandRequirements.value.wants)
						? brandRequirements.value.wants
						: [];

			const badsDraftValue =
				snapshotRequirements && Array.isArray(snapshotRequirements.bads)
					? snapshotRequirements.bads
					: Array.isArray(brandRequirements.value.bads)
						? brandRequirements.value.bads
						: [];

			// Build context from the snapshot override (when provided), not from current reactive state.
			if (isRequest.value) {
				const selectedIds = normaliseIdList(partnershipsDraftValue);

				const labelById = new Map(
					(Array.isArray(whatPartnershipsDropdown.value) ? whatPartnershipsDropdown.value : []).map((item) => [item.id, item.label])
				);

				const collaborationTypeLabels = selectedIds
					.map((id) => labelById.get(id))
					.filter((label) => typeof label === "string" && label.trim().length);

				briefWriterContext.value = {
					mode: "request",
					recipientBrandId: resolvedConnecteeId.value,
					collaborationTypeIds: selectedIds,
					collaborationTypeLabels,
					messageDraft: messageDraftValue,
				};
			} else {
				briefWriterContext.value = {
					mode: "feature",
					titleDraft: titleDraftValue,
					contentDraft: messageDraftValue,
					wants: wantsDraftValue,
					bads: badsDraftValue,
				};
			}

			const draft = isRequest.value
				? { requestText: messageDraftValue }
				: {
						featureTitle: titleDraftValue,
						featureContent: messageDraftValue,
						lookingForText: Array.isArray(wantsDraftValue) ? wantsDraftValue.join("\n") : "",
						notLookingForText: Array.isArray(badsDraftValue) ? badsDraftValue.join("\n") : "",
					};

			try {
				const response = await ApiService.get("matching/brief-writer/assist/run-details", {
					params: { runId: cachedRunId },
				});

				const data = response && response.data ? response.data : null;
				if (!data || typeof data !== "object") return;
				if (data.briefWriterCostInfo) {
					console.log("[WritingTool][assist/run-details] cost info", data.briefWriterCostInfo);
				}

				briefWriterResumePayload.value = {
					...data,
					resume: true,
					cursor: bw.cursor || null,
					refineNotesDraft: typeof bw.refineNotesDraft === "string" ? bw.refineNotesDraft : "",
					draft,
				};

				showBriefWriter.value = true;
				isPitchAssistantOpen.value = !!bw.open;
			} catch (err) {
				console.error("BrandConnect.restoreBriefWriterFromCache error:", err);
			}
		};

		const isMobile = computed(() => {
			if (typeof window === "undefined") return false;
			return window.innerWidth <= 767;
		});

		const isBelowDesktop = computed(() => {
			if (typeof window === "undefined") return false;
			return window.innerWidth < 1024;
		});

		const isSingleSourceEditingActive = computed(() => SINGLE_SOUCE_EDITING && isPitchAssistantOpen.value);

		const selectedPartnershipLabels = computed(() => {
			const labelById = new Map(
				(Array.isArray(whatPartnershipsDropdown.value) ? whatPartnershipsDropdown.value : []).map((item) => [item.id, item.label])
			);

			return (Array.isArray(partnershipInMind.value) ? partnershipInMind.value : [])
				.map((id) => {
					const label = labelById.get(id);
					if (typeof label === "string" && label.trim().length > 0) return label;
					return String(id);
				})
				.filter((label) => label && label.trim().length > 0);
		});

		const requestMessageTextOnlyLabel = computed(() => {
			return typeof message.value === "string" && message.value.trim().length > 0 ? "Your message" : "";
		});

		const featureTextOnlyLabel = computed(() => {
			const hasTitle = typeof title.value === "string" && title.value.trim().length > 0;
			const hasMessage = typeof message.value === "string" && message.value.trim().length > 0;
			const hasWants = Array.isArray(brandRequirements.value.wants) && brandRequirements.value.wants.length > 0;
			const hasBads = Array.isArray(brandRequirements.value.bads) && brandRequirements.value.bads.length > 0;
			return hasTitle || hasMessage || hasWants || hasBads ? "Your opportunity" : "";
		});

		const showLockedDraftEmptyState = computed(() => {
			if (!isSingleSourceEditingActive.value) return false;
			if (isRequest.value) {
				return !(typeof message.value === "string" && message.value.trim().length > 0);
			}
			const hasTitle = typeof title.value === "string" && title.value.trim().length > 0;
			const hasMessage = typeof message.value === "string" && message.value.trim().length > 0;
			const hasWants = Array.isArray(brandRequirements.value.wants) && brandRequirements.value.wants.length > 0;
			const hasBads = Array.isArray(brandRequirements.value.bads) && brandRequirements.value.bads.length > 0;
			return !hasTitle && !hasMessage && !hasWants && !hasBads;
		});

		const lockedDraftEmptyStateLabel = computed(() => {
			return isRequest.value ? "Request draft" : "Opportunity draft";
		});

		const showLockedDraftContentBox = computed(() => {
			if (!isSingleSourceEditingActive.value) return false;
			if (showLockedDraftEmptyState.value) return false;
			if (isRequest.value) {
				return typeof message.value === "string" && message.value.trim().length > 0;
			}
			const hasTitle = typeof title.value === "string" && title.value.trim().length > 0;
			const hasMessage = typeof message.value === "string" && message.value.trim().length > 0;
			const hasWants = Array.isArray(brandRequirements.value.wants) && brandRequirements.value.wants.length > 0;
			const hasBads = Array.isArray(brandRequirements.value.bads) && brandRequirements.value.bads.length > 0;
			return hasTitle || hasMessage || hasWants || hasBads;
		});

		const scrollToBriefWriter = async () => {
			if (!allowBriefWriterAutoScroll.value) return;
			if (!isPitchAssistantOpen.value) return;
			allowBriefWriterAutoScroll.value = false;

			await nextTick();
			if (typeof document === "undefined") return;
			const controls = document.getElementById("brand-connect-assistant-controls");
			const panel = document.getElementById("brand-connect-ai-panel");
			const target = controls || panel;
			if (target && typeof target.scrollIntoView === "function") {
				target.scrollIntoView({ behavior: "smooth", block: "start" });
			}
		};

		const scrollToGeneratedContentStart = async () => {
			if (!isBelowDesktop.value) return;

			await nextTick();
			if (typeof document === "undefined") return;

			const selector = isRequest.value ? ".message-label" : ".brand-connect-main-title";
			const target = document.querySelector(`.modal.brand-connect-main-pupup ${selector}`);
			if (target && typeof target.scrollIntoView === "function") {
				target.scrollIntoView({ behavior: "smooth", block: "start", inline: "nearest" });
			}
		};

		const brandRequirements = ref({
			want: "",
			bad: "",
			wants: [],
			bads: [],
		});

		const showContactPopup = ref(false);
		const customContact = ref({
			fname: "",
			sname: "",
			email: "",
		});

		const lifestyleImage = ref("");
		const showUploader = ref(false);
		const uploaderFormData = ref({});

		const updateLoadingMessage = async (message, source = "system") => {
			const wasBriefWriterLoading = !!loadingMessage.value && loadingMessageSource.value === "brief-writer";
			const shouldScrollToGeneratedContentStart = !message && source === "brief-writer" && wasBriefWriterLoading && isBelowDesktop.value;

			loadingMessage.value = message || "";
			loadingMessageSource.value = message ? source : "";
			if (!message) {
				loadingAnimation.value = "";
			} else if (showBriefWriter.value) {
				loadingAnimation.value = "ai-stars";
			} else {
				loadingAnimation.value = "";
			}
			if (message) {
				scrollTrigger.value++;

				// Scroll the textarea to top
				await nextTick();
				const textarea = document.querySelector('textarea[name="brand-connect-message"]');
				if (textarea) {
					textarea.scrollTop = 0;
				}
			} else if (shouldScrollToGeneratedContentStart) {
				await scrollToGeneratedContentStart();
			}
		};

		const contactDetailsErrorMessage = "Please don't add contact details here";
		const contactDetailsToastMessage = "Please remove the contact details";

		const syncMessageEmailError = (value) => {
			if (typeof value !== "string") {
				messageEmailError.value = "";
				return;
			}
			const matches = value.match(messageEmailPattern) || [];
			if (matches.length > 0) {
				messageEmailError.value = contactDetailsErrorMessage;
			} else if (messageEmailError.value) {
				messageEmailError.value = "";
			}
		};

		const handleMessageEmailFound = (payload) => {
			if (!payload || typeof payload !== "object") return;
			if (payload.found) {
				messageEmailError.value = contactDetailsErrorMessage;
			} else if (messageEmailError.value) {
				messageEmailError.value = "";
			}
		};

		const uploaderTitle = computed(() => {
			return "Upload Image";
		});

		const uploaderDragText = computed(() => {
			return "Drag and drop your image here or click to browse";
		});

		const customContactMessage = computed(() => {
			if (!loaded.value) return "";
			return `Pitches will go to ${customContact.value.fname || connectorInfo.value.user.fname} ${customContact.value.sname || connectorInfo.value.user.sname} <${customContact.value.email || connectorInfo.value.user.email}>`;
		});

		const activeBrand = computed(() => {
			return isRequest.value ? connecteeInfo.value : connectorInfo.value;
		});

		const pitchAssistantOptions = [
			"Writer's block? Give our Writing Tool a go!",
			"Stuck for words? Let our Writing Tool help!",
			"Need a hand? Our Writing Tool is here to help!",
			"Not sure what to say? Our Writing Tool can help!",
			"Let our Writing Tool write the first draft for you!",
		];

		const randomPitchAssistantText = ref(pitchAssistantOptions[Math.floor(Math.random() * pitchAssistantOptions.length)]);

		const hasContent = computed(() => {
			if (isRequest.value) {
				return partnershipInMind.value.length > 0 || message.value.trim().length > 0;
			}
			return brandRequirements.value.wants.length > 0 || brandRequirements.value.bads.length > 0 || message.value.trim().length > 0;
		});

		watch(hasContent, () => {
			randomPitchAssistantText.value = pitchAssistantOptions[Math.floor(Math.random() * pitchAssistantOptions.length)];
		});

		const aiAssistPara = computed(() => {
			if (showBriefWriter.value) {
				return "**Edit the copy yourself or give our Writing Tool some pointers on edits you'd like below**";
			}
			if (isRequest.value) {
				if (!hasContent.value) {
					return "Clicking our Writing Tool button without adding any info can create a bit of AI magic, give it a go!";
				}

				return randomPitchAssistantText.value;
			}
			if (!hasContent.value) {
				return "Clicking our Writing Tool button without adding any info can create a bit of AI magic, give it a go!";
			}

			return randomPitchAssistantText.value;
		});

		const shouldShowTryMeNudge = computed(() => !hasOpenedWritingTool.value && !isPitchAssistantOpen.value);

		const writingToolAssistantNote = computed(() => {
			if (briefWriterUiState.value.hasTimeline) {
				//return isRequest.value ? "Use these tips to improve the draft you already have." : "Use these tips to refine your current opportunity draft.";
			} else {
				return isRequest.value
					? "Create a clear, compelling collab request in minutes."
					: "Create a clear, compelling opportunity in minutes.";
			}
		});

		const writingToolHintItems = computed(() => {
			const hasDraft = !!briefWriterUiState.value.hasTimeline;
			if (!hasDraft) {
				if (isRequest.value) {
					return [
						"<strong>Partnership types:</strong> Select the partnership type(s) you're interested in from the dropdown if you know what you're looking for, the Writing Tool will take this into account when creating your request.",
						'<strong>Your message:</strong> If you have an idea of what you want to say, add it to the "Now write them a nice message" box, it doesn\'t have to be perfect, the Writing Tool will help you refine it.',
						"<strong>Pointers:</strong> If you have any specific pointers/guidance for the tool to follow, add them below (this can be added as well as your message or on it's own.",
						"<strong>Quick start:</strong> Use these instead of writing pointers to get going quickly with your first draft.",
					];
				}
				return [
					"<strong>Your opportunity info:</strong> Fill in the title, message, and want/not want lists if you have any info to give the tool some context (the tool can create a draft from scratch if you leave any/all of these fields blank).",
					"<strong>Pointers:</strong> If you have any specific pointers/guidance for the tool to follow, add them below (this can be added as well as your opportunity info or on it's own).",
				];
			}

			if (isRequest.value) {
				return [
					"<strong>Feedback on what I did...:</strong> The writing tool will give you feedback on what it did, you can use this to guide your edits or use it as a starting point for your own edits.",
					"<strong>Quick edits:</strong> One click edits to request info.",
					"<strong>Edit directly:</strong> Edit the message directly, your manual edits are always allowed.",
					"<strong>Refine copy:</strong> Use Refine copy to ask for targeted changes to the current draft (tone, length, structure, detail).",
				];
			}

			return [
				"<strong>Feedback on what I did...:</strong> The writing tool will give you feedback on what it did, you can use this to guide your edits or use it as a starting point for your own edits.",
				"<strong>Quick edits:</strong> One click edits to opportunity info.",
				"<strong>Edit directly:</strong> Edit the title, message, and want/not want lists directly at any time, your manual edits are always allowed.",
				"<strong>Refine copy:</strong> Use Refine copy to ask for targeted changes to the current draft (tone, length, structure, detail).",
			];
		});

		const toggleWritingToolHints = () => {
			showWritingToolHints.value = !showWritingToolHints.value;
		};

		const formatField = (fieldName) => {
			if (!activeBrand.value || !activeBrand.value[fieldName]) return "";
			const value = activeBrand.value[fieldName];
			return Array.isArray(value) ? value.join(", ") : value;
		};

		const websiteLabel = computed(() => {
			if (!activeBrand.value || !activeBrand.value.brand_website) return "";
			try {
				const url = new URL(activeBrand.value.brand_website);
				return url.hostname.replace(/^www\./, "");
			} catch (e) {
				return activeBrand.value.brand_website;
			}
		});

		const instagramFollowersText = computed(() => formatField("instagram_followers"));
		const emailSubscribersText = computed(() => formatField("email_database"));
		const facebookFollowersText = computed(() => formatField("facebook_followers"));
		const tiktokFollowersText = computed(() => formatField("tiktok_followers"));

		const getConnectorInfo = async () => {
			briefWriterRequestSeq.value++;
			const currentSeq = briefWriterRequestSeq.value;
			loadError.value = "";
			if (!loaded.value) {
				updateLoadingMessage("Loading brand details…");
			}

			try {
				const response = await ApiService.get(
					`brands/connect-brand-details${resolvedConnecteeId.value ? `/${resolvedConnecteeId.value}` : ""}`
				);

				if (currentSeq !== briefWriterRequestSeq.value) return;

				connectorInfo.value = response.data.connectorInfo;
				connecteeInfo.value = {
					...initialConnecteePreview(),
					...(response.data.connecteeInfo || {}),
				};
				loaded.value = true;
				updateLoadingMessage();

				refreshBriefWriterContext();

				applyInitialFeatureDraft();

				if (pendingSubscriptionRefresh.value && subscription.value.active && isRequest.value) {
					pendingSubscriptionRefresh.value = false;
					await getConnectorInfo();
				}
			} catch (err) {
				if (currentSeq !== briefWriterRequestSeq.value) return;
				loaded.value = false;
				updateLoadingMessage();
				loadError.value =
					err?.response?.data?.error || err?.response?.data?.message || "We couldn't load this brand right now. Please try again.";
				console.error("BrandConnect.getConnectorInfo error:", err);
			} finally {
				if (currentSeq === briefWriterRequestSeq.value && !loadingMessage.value && !loaded.value && !loadError.value) {
					loadError.value = "We couldn't load this brand right now. Please try again.";
				}
			}
		};

		const buildRequestContext = (selectedIds) => {
			const labelById = new Map(
				(Array.isArray(whatPartnershipsDropdown.value) ? whatPartnershipsDropdown.value : []).map((item) => [item.id, item.label])
			);

			const collaborationTypeLabels = selectedIds
				.map((id) => labelById.get(id))
				.filter((label) => typeof label === "string" && label.trim().length);

			return {
				mode: "request",
				recipientBrandId: resolvedConnecteeId.value,
				collaborationTypeIds: selectedIds,
				collaborationTypeLabels,
				messageDraft: message.value || "",
			};
		};

		const buildFeatureContext = () => {
			const wants = Array.isArray(brandRequirements.value.wants) ? brandRequirements.value.wants : [];
			const bads = Array.isArray(brandRequirements.value.bads) ? brandRequirements.value.bads : [];

			return {
				mode: "feature",
				titleDraft: title.value || "",
				contentDraft: message.value || "",
				wants,
				bads,
			};
		};

		const refreshBriefWriterContext = () => {
			if (!loaded.value) return;

			if (isRequest.value) {
				const selectedIds = Array.isArray(partnershipInMind.value) ? partnershipInMind.value : [];
				briefWriterContext.value = buildRequestContext(selectedIds);
				return;
			}

			briefWriterContext.value = buildFeatureContext();
		};

		const handleAiAssist = async () => {
			if (!loaded.value) return;

			briefWriterResumePayload.value = null;

			if (showBriefWriter.value) {
				if (briefWriterUiState.value.isBaselineCursor) {
					const instance = writingToolRef.value;
					if (instance && typeof instance.resetAndRun === "function") {
						instance.resetAndRun();
					}

					markWritingToolOpened();
					isPitchAssistantOpen.value = true;
					return;
				}

				markWritingToolOpened();
				isPitchAssistantOpen.value = true;
				return;
			}

			if (isRequest.value) {
				const selectedIds = Array.isArray(partnershipInMind.value) ? partnershipInMind.value : [];
				briefWriterContext.value = buildRequestContext(selectedIds);
			} else {
				briefWriterContext.value = buildFeatureContext();
			}

			markWritingToolOpened();
			isPitchAssistantOpen.value = true;
			showBriefWriter.value = true;
		};

		const markWritingToolOpened = () => {
			if (hasOpenedWritingTool.value) return;

			store.commit("general/updateVar", {
				varName: "hasOpenedWritingTool",
				info: true,
			});

			ApiService.post("users/action/writing-tool-opened").catch((err) => {
				console.error("BrandConnect.markWritingToolOpened error:", err);
			});
		};

		const togglePitchAssistant = () => {
			const willOpen = !isPitchAssistantOpen.value;
			if (willOpen) {
				markWritingToolOpened();
			}
			isPitchAssistantOpen.value = !isPitchAssistantOpen.value;
		};

		const handleAssistantPanelKeydown = (event) => {
			if (isPitchAssistantOpen.value) return;
			if (!event) return;
			if (event.key !== "Enter" && event.key !== " ") return;

			event.preventDefault();
			if (requestAccessMode.value === "inactive_anonymous") {
				handleLockedWritingToolClick();
				return;
			}
			togglePitchAssistant();
		};

		const handleBriefWriterUiState = (state) => {
			const s = state && typeof state === "object" ? state : {};

			const runIdRaw = s.runId != null ? parseInt(s.runId, 10) : null;
			const runId = Number.isFinite(runIdRaw) ? runIdRaw : null;

			briefWriterUiState.value = {
				hasTimeline: !!s.hasTimeline,
				isBaselineCursor: !!s.isBaselineCursor,
				runId,
				canUndo: !!s.canUndo,
				canRedo: !!s.canRedo,
			};
		};

		const undoHistory = async () => {
			const prevIndex = findPrevDistinctIndex(cacheHistoryCursor.value, cacheHistoryEntries.value);
			if (prevIndex === null) return;
			await setHistoryCursor(prevIndex);
		};

		const redoHistory = async () => {
			const nextIndex = findNextDistinctIndex(cacheHistoryCursor.value, cacheHistoryEntries.value);
			if (nextIndex === null) return;
			await setHistoryCursor(nextIndex);
		};

		const resetLocalFields = () => {
			message.value = "";
			title.value = "";
			partnershipInMind.value = [];
			brandRequirements.value = {
				want: "",
				bad: "",
				wants: [],
				bads: [],
			};
			lifestyleImage.value = "";
			customContact.value = {
				fname: "",
				sname: "",
				email: "",
			};
			featurePitchId.value = null;
		};

		const startAgainHistory = async () => {
			if (!cacheEnabled.value) return;

			suppressCacheDirty.value = true;
			resetLocalFields();
			briefWriterResumePayload.value = null;
			briefWriterUiState.value = {
				hasTimeline: false,
				isBaselineCursor: false,
				runId: null,
				canUndo: false,
				canRedo: false,
			};
			await nextTick();
			suppressCacheDirty.value = false;

			const instance = writingToolRef.value;
			if (instance && typeof instance.startAgain === "function") {
				await instance.startAgain();
			}

			markCacheDirty();
			await appendHistoryEntry("start-again", { force: true });
		};

		const saveCacheManually = async () => {
			await saveBrandConnectCache({ reason: "manual", force: true });
		};

		const handleBriefWriterCacheState = (state) => {
			const s = state && typeof state === "object" ? state : {};

			const nextRunIdRaw = s.runId != null ? parseInt(s.runId, 10) : null;
			const nextRunId = Number.isFinite(nextRunIdRaw) ? nextRunIdRaw : null;

			const nextType = s.type === "feature" ? "feature" : "request";

			const nextCursor =
				s.cursor && typeof s.cursor === "object"
					? {
							kind: s.cursor.kind === "iteration" ? "iteration" : s.cursor.kind === "base" ? "base" : "baseline",
							iterationIndex:
								s.cursor.iterationIndex === 0 || s.cursor.iterationIndex === "0"
									? 0
									: s.cursor.iterationIndex != null
										? parseInt(s.cursor.iterationIndex, 10)
										: null,
						}
					: null;

			const nextRefineNotesDraft = typeof s.refineNotesDraft === "string" ? s.refineNotesDraft : "";

			const current = briefWriterCacheState.value && typeof briefWriterCacheState.value === "object" ? briefWriterCacheState.value : {};
			const next = {
				open: !!isPitchAssistantOpen.value,
				runId: nextRunId,
				type: nextType,
				cursor: nextCursor,
				refineNotesDraft: nextRefineNotesDraft,
				updatedAt: new Date().toISOString(),
			};

			let changed = false;
			try {
				changed = JSON.stringify(current || {}) !== JSON.stringify(next || {});
			} catch (e) {
				changed = true;
			}

			briefWriterCacheState.value = next;

			if (changed && !isApplyingHistory.value && Date.now() >= suppressAutoSaveUntil.value) {
				markCacheDirty();
			}
		};

		const handleBriefWriterUpdate = async (payload) => {
			if (!payload || typeof payload !== "object") return;

			// Ignore flush events emitted during history application so we don't clear the form/state.
			if (isApplyingHistory.value && payload.flush) return;
			if (payload._source === "resume-draft") return;

			suppressAutoFocus.value = true;

			if (payload.flush) {
				briefWriterResumePayload.value = null;
				briefWriterUiState.value = {
					hasTimeline: false,
					isBaselineCursor: false,
					runId: null,
					canUndo: false,
					canRedo: false,
				};
			}

			// Skip typewriter effect for AI-generated content updates
			messageTypeEffectSkipKey.value += 1;

			if (payload.mode === "request") {
				if (typeof payload.message === "string") {
					message.value = payload.message;
				}
			} else {
				if (typeof payload.title === "string") {
					title.value = payload.title;
				}
				if (typeof payload.content === "string") {
					message.value = payload.content;
				}

				const wants = Array.isArray(payload.wants) ? payload.wants : [];
				const bads = Array.isArray(payload.bads) ? payload.bads : [];

				brandRequirements.value.wants = wants.slice(0, 5);
				brandRequirements.value.bads = bads.slice(0, 5);
			}

			await nextTick();
			suppressAutoFocus.value = false;

			if (!payload.flush && !isApplyingHistory.value && Date.now() >= suppressAutoSaveUntil.value) {
				markCacheDirty();
				triggerImmediateSave("brief-writer-update");
			}
		};

		/**
		 * Open subscription modal from within BrandConnect
		 */
		const openRequestSubscriptionModal = async () => {
			const title =
				resolvedInitialData.value?.requestMeta?.subscriptionTitle ||
				subscription.value?.notAllowedMessage?.request ||
				"Upgrade to send requests";

			const paymentRedirect = resolvedInitialData.value?.requestMeta?.paymentRedirect || null;

			await saveBrandConnectCache({ reason: "upgrade", force: true });

			TokenService.saveBrandConnectResume({
				connecteeId: resolvedConnecteeId.value,
				initialData: {
					...(resolvedInitialData.value || {}),
					requestMeta: {
						...(resolvedInitialData.value?.requestMeta || {}),
						brandNameVisible: brandNameVisible.value,
						subscriptionTitle: title,
						paymentRedirect,
					},
					message: message.value || "",
					partnershipInMind: Array.isArray(partnershipInMind.value) ? [...partnershipInMind.value] : [],
				},
				source: brandConnectModal.value?.source || "brands",
				userBrandId: connectorInfo.value?.brand_id || null,
				savedAt: Date.now(),
			});

			store.commit("general/showSubscriptionPupup", {
				show: true,
				title,
				paymentRedirect,
			});
		};

		/**
		 * Handle click on writing tool when it's locked (inactive anonymous)
		 */
		const handleLockedWritingToolClick = () => {
			if (requestAccessMode.value !== "inactive_anonymous") return;
			showWritingToolLockedNotice.value = true;
		};

		const handleLockedDraftEditAttempt = (event) => {
			if (!isSingleSourceEditingActive.value) return;

			if (event && event.type === "keydown") {
				const isActivationKey = event.key === "Enter" || event.key === " " || event.key === "Spacebar";
				if (!isActivationKey || event.target !== event.currentTarget) return;
				event.preventDefault();
			}

			$toast.success("Close the writing tool to edit directly");
		};

		const close = async (sendRequest = false) => {
			const shouldSendRequest = sendRequest === true;
			const hasMessage = !!(typeof message.value === "string" && message.value.trim());
			const assistantWasOpen = !!isPitchAssistantOpen.value;
			initialFeatureDraftApplied.value = false;

			// Block send for inactive users - should not reach here but safety check
			if (shouldSendRequest && isInactiveRequestUser.value) {
				openRequestSubscriptionModal();
				return;
			}

			if (shouldSendRequest && messageEmailError.value) {
				syncMessageEmailError(message.value);
				$toast.error(contactDetailsToastMessage);
				return;
			}
			if (shouldSendRequest && !hasMessage) {
				$toast.error(requestValidationMessage);
				return;
			}
			await saveBrandConnectCache({ reason: "close" });
			showBriefWriter.value = false;
			isPitchAssistantOpen.value = false;
			briefWriterContext.value = null;
			if (assistantWasOpen) {
				await nextTick();
				await saveLatestBrandConnectCache();
			}
			const capturedBriefWriterRunId = briefWriterUiState.value?.runId || null;
			briefWriterUiState.value = {
				hasTimeline: false,
				isBaselineCursor: false,
				runId: null,
				canUndo: false,
				canRedo: false,
			};
			let brandConnectInfo = null;
			if (shouldSendRequest) {
				brandConnectInfo = {
					brandId: resolvedConnecteeId.value,
					message: message.value,
					partnerships: partnershipInMind.value,
					briefWriterRunId: capturedBriefWriterRunId,
				};
			}
			emit("close", brandConnectInfo);
			store.commit("general/updateVar", {
				varName: "brandConnectModal",
				info: { show: false, connecteeId: null, initialData: null, source: "" },
			});
		};

		const previewFeature = async () => {
			if (!isFeature.value) return;

			commitPendingRequirementInputs();

			if (messageEmailError.value) {
				syncMessageEmailError(message.value);
				$toast.error(contactDetailsToastMessage);
				return;
			}

			// Validate required fields
			if (!title.value) {
				$toast.error("Please enter a title for your opportunity");
				return;
			}
			if (!message.value) {
				$toast.error("Please enter a description for your opportunity");
				return;
			}
			if (!brandRequirements.value.wants.length) {
				$toast.error("Please add at least one item to 'What you're looking for'");
				return;
			}
			if (!connectorInfo.value?.brand_logo && !connectorInfo.value?.brand_logo_pending) {
				$toast.error("Please upload your brand logo");
				return;
			}
			if (!lifestyleImage.value) {
				$toast.error("Please upload a lifestyle image");
				return;
			}

			await saveBrandConnectCache({ reason: "preview", force: true });

			// Emit the feature data
			emit("preview-feature", {
				title: title.value,
				message: message.value,
				wants: brandRequirements.value.wants,
				bads: brandRequirements.value.bads,
				lifestyleImage: lifestyleImage.value,
				customContact: customContact.value,
				isEditing: !!(normalizedFeatureInitialData.value.pitch_id != null && persistedFeatureData.value),
				originalData: normalizedFeatureInitialData.value.pitch_id != null && persistedFeatureData.value ? persistedFeatureData.value : null,
				persistedData: normalizedFeatureInitialData.value.pitch_id != null && persistedFeatureData.value ? persistedFeatureData.value : null,
				pitchId: featurePitchId.value,
				pitch_id: featurePitchId.value,
				pending: !!normalizedFeatureInitialData.value.pending,
				approved: !!normalizedFeatureInitialData.value.approved,
				paid: !!normalizedFeatureInitialData.value.paid,
				briefWriterRunId: briefWriterUiState.value?.runId || null,
				current: !!normalizedFeatureInitialData.value.current,
				_id: normalizedFeatureInitialData.value._id,
			});
		};

		const addItemToList = (listType) => {
			const listKey = listType + "s";
			const pendingText = typeof brandRequirements.value[listType] === "string" ? brandRequirements.value[listType].trim() : "";
			const list = Array.isArray(brandRequirements.value[listKey]) ? brandRequirements.value[listKey] : [];

			if (pendingText && list.length < 5) {
				list.push(pendingText);
				brandRequirements.value[listKey] = list;
				brandRequirements.value[listType] = "";
				markCacheDirty();
				triggerImmediateSave("requirements");
			}
		};

		const commitPendingRequirementInputs = () => {
			addItemToList("want");
			addItemToList("bad");
		};

		const deleteItem = (listType, item) => {
			const list = brandRequirements.value[listType];
			list.splice(list.indexOf(item), 1);
			markCacheDirty();
			triggerImmediateSave("requirements");
		};

		const updateWants = (newValue) => {
			brandRequirements.value.wants = newValue;
			markCacheDirty();
			triggerImmediateSave("requirements");
		};

		const updateBads = (newValue) => {
			brandRequirements.value.bads = newValue;
			markCacheDirty();
			triggerImmediateSave("requirements");
		};

		const saveCustomContact = () => {
			// Validate fields
			if (!customContact.value.fname || !customContact.value.sname || !customContact.value.email) {
				$toast.error("Please fill in all fields.");
				return;
			}
			if (customContact.value.email === connectorInfo.value.user.email) {
				customContact.value.email = "";
			}
			if (customContact.value.fname === connectorInfo.value.user.fname && customContact.value.sname === connectorInfo.value.user.sname) {
				customContact.value.fname = "";
				customContact.value.sname = "";
			}
			showContactPopup.value = false;
			// The customContact data will be used when submitting the feature request
		};

		const cancelCustomContact = () => {
			showContactPopup.value = false;
		};

		const uploadImage = (imageType) => {
			uploaderFormData.value = {
				type: "brand-image",
				vars: { imageType: imageType },
			};
			showUploader.value = true;
		};

		const closeUploader = (response) => {
			if (response) {
				if (response.error) {
					$toast.error(response.error);
				} else {
					$toast.success("Image uploaded successfully");
					if (response.type === "pitch-lifestyle") {
						lifestyleImage.value = response.file;
					}

					const pitchIdRaw = response.pitchId;
					const pitchIdParsed = pitchIdRaw != null ? parseInt(pitchIdRaw, 10) : null;
					if (Number.isFinite(pitchIdParsed)) {
						featurePitchId.value = pitchIdParsed;
					}

					markCacheDirty();
					triggerImmediateSave("lifestyle-image");
				}
			}
			showUploader.value = false;
		};

		const whatPartnershipsDropdown = computed(() => {
			if (!connecteeInfo.value || !connecteeInfo.value.what_partnerships) return [];
			let list = [];
			connecteeInfo.value.what_partnerships.forEach(function (item) {
				let found = list.find((row) => row.id === item.id);
				if (!found) list.push(item);
			});
			list.sort(function (a, b) {
				if (a.label < b.label) return -1;
				if (a.label > b.label) return 1;
				return 0;
			});
			return list;
		});

		watch(message, (value) => {
			markCacheDirty();
			// Keep email validation in sync for programmatic updates (e.g. writing tool).
			syncMessageEmailError(value);
		});
		watch(title, markCacheDirty);
		watch(featurePitchId, markCacheDirty);
		watch(partnershipInMind, markCacheDirty, { deep: true });
		watch(brandRequirements, markCacheDirty, { deep: true });
		watch(lifestyleImage, markCacheDirty);
		watch(customContact, markCacheDirty, { deep: true });

		watch(cacheBadgeText, (next, prev) => {
			if (next === "Saved" && prev !== "Saved") {
				cacheBadgeTooltipReady.value = false;
			}
		});

		watch(showBriefWriter, (next, prev) => {
			if (next === prev) return;

			const current = briefWriterCacheState.value && typeof briefWriterCacheState.value === "object" ? briefWriterCacheState.value : {};
			const nextState = { ...current, open: !!isPitchAssistantOpen.value, updatedAt: new Date().toISOString() };

			briefWriterCacheState.value = nextState;

			// Opening/closing the writing tool should not mark the cache dirty or create history entries.
		});

		watch(isPitchAssistantOpen, (next, prev) => {
			if (next === prev) return;

			const current = briefWriterCacheState.value && typeof briefWriterCacheState.value === "object" ? briefWriterCacheState.value : {};
			const nextState = { ...current, open: !!next, updatedAt: new Date().toISOString() };

			briefWriterCacheState.value = nextState;

			// Opening/closing the writing tool should not mark the cache dirty or create history entries.
		});

		let autosaveIntervalId = null;

		watch(message, refreshBriefWriterContext);
		watch(title, refreshBriefWriterContext);
		watch(partnershipInMind, refreshBriefWriterContext, { deep: true });
		watch(brandRequirements, refreshBriefWriterContext, { deep: true });
		watch(whatPartnershipsDropdown, refreshBriefWriterContext, { deep: true });

		// Re-fetch brand details when subscription becomes active (for anonymous -> visible upgrade)
		watch(
			() => subscription.value.active,
			async (newActive, oldActive) => {
				if (!isRequest.value || !newActive || oldActive) return;
				pendingSubscriptionRefresh.value = true;
				showWritingToolLockedNotice.value = false;
				if (!loaded.value) return;
				await getConnectorInfo();
				refreshBriefWriterContext();
				await restoreBriefWriterFromCache();
			}
		);

		watch(requestAccessMode, async (newMode, oldMode) => {
			if (!isRequest.value || newMode !== "active" || oldMode === "active") return;
			showWritingToolLockedNotice.value = false;
			await restoreBriefWriterFromCache();
		});

		watch(featureInitialDataKey, (newKey, oldKey) => {
			if (newKey === oldKey) return;
			initialFeatureDraftApplied.value = false;
			persistedFeatureData.value = isFeature.value && normalizedFeatureInitialData.value.pitch_id != null ? buildPersistedFeatureData() : null;
			applyInitialFeatureDraft();
			refreshBriefWriterContext();
		});

		onMounted(async () => {
			if (isFeature.value && normalizedFeatureInitialData.value.pitch_id != null) {
				persistedFeatureData.value = buildPersistedFeatureData();
			}
			await getConnectorInfo();
			await loadBrandConnectHistory();
			await loadBrandConnectCache();
			refreshBriefWriterContext();
			await restoreBriefWriterFromCache();

			autosaveIntervalId = setInterval(() => {
				saveBrandConnectCache({ reason: "autosave" });
			}, CACHE_AUTOSAVE_MS);

			// Track metadata for feedback widget
			if (props.connecteeId) {
				store.commit("feedback/setMetadata", {
					key: "connectingToBrandId",
					value: props.connecteeId,
				});
				store.commit("feedback/setOpenWindow", "BrandConnect");
			}
		});

		onUnmounted(() => {
			if (autosaveIntervalId) {
				clearInterval(autosaveIntervalId);
				autosaveIntervalId = null;
			}
			if (immediateSaveTimer.value) {
				clearTimeout(immediateSaveTimer.value);
				immediateSaveTimer.value = null;
			}

			// Clear metadata for feedback widget
			store.commit("feedback/clearMetadata", "connectingToBrandId");
			store.commit("feedback/removeOpenWindow", "BrandConnect");
		});

		return {
			close,
			subscription,
			connectorInfo,
			connecteeInfo,
			isRequest,
			isFeature,
			featureAwaitingApproval,
			requestAccessMode,
			isInactiveRequestUser,
			brandNameVisible,
			writingToolLockedNoticeMessage,
			showWritingToolLockedUpgradeButton,
			showWritingToolLockedNotice,
			loadError,
			openRequestSubscriptionModal,
			handleLockedWritingToolClick,
			handleLockedDraftEditAttempt,
			loaded,
			loadingMessage,
			loadingMessageSource,
			loadingAnimation,
			scrollTrigger,
			message,
			messageTypeEffectSkipKey,
			isSingleSourceEditingActive,
			selectedPartnershipLabels,
			requestMessageTextOnlyLabel,
			featureTextOnlyLabel,
			showLockedDraftEmptyState,
			lockedDraftEmptyStateLabel,
			showLockedDraftContentBox,
			activeBrand,
			hasBrandLogoBgColour,
			brandLogoCellStyles,
			websiteLabel,
			instagramFollowersText,
			emailSubscribersText,
			facebookFollowersText,
			tiktokFollowersText,
			getConnectorInfo,
			handleAiAssist,
			handleBriefWriterUiState,
			whatPartnershipsDropdown,
			partnershipInMind,
			isMobile,
			isBelowDesktop,
			showBriefWriter,
			isPitchAssistantOpen,
			tryMeImage,
			shouldShowTryMeNudge,
			briefWriterContext,
			briefWriterResumePayload,
			showWritingToolHints,
			writingToolAssistantNote,
			writingToolHintItems,
			toggleWritingToolHints,
			handleBriefWriterCacheState,
			writingToolRef,
			briefWriterUiState,
			undoHistory,
			redoHistory,
			startAgainHistory,
			saveCacheManually,
			canHistoryUndo,
			canHistoryRedo,
			cacheDirty,
			cacheSaving,
			cacheHistoryEntries,
			cacheBadgeTooltipReady,
			onCacheBadgeEnter,
			armCacheBadgeTooltip,
			onHistoryTooltipEnter,
			onHistoryTooltipLeave,
			shouldShowHistoryTooltip,
			suppressAutoFocus,
			handleBriefWriterUpdate,
			brandRequirements,
			addItemToList,
			deleteItem,
			updateWants,
			updateBads,
			title,
			showContactPopup,
			customContact,
			saveCustomContact,
			cancelCustomContact,
			customContactMessage,
			lifestyleImage,
			showUploader,
			uploaderFormData,
			uploaderTitle,
			uploaderDragText,
			uploadImage,
			closeUploader,
			previewFeature,
			updateLoadingMessage,
			aiAssistPara,
			togglePitchAssistant,
			handleAssistantPanelKeydown,
			cacheBadgeText,
			cacheBadgeHasTooltip,
			cacheBadgeTooltipId,
			showCacheStatusUi,
			hasContent,
			messageEmailError,
			handleMessageEmailFound,
		};
	},
};
</script>
<style lang="less" scoped>
@import "./../../assets/css/less/_settings.less";
@import "./../../assets/css/less/_helper.less";
.brand-logo {
	width: 130px;
	margin-right: 20px;
}
.brand-connect-header-title {
	color: rgba(162, 108, 103, 0.7);
    margin: 0 0 10px;
}
.brand-connect-header {
	position: relative;
	display: flex;
	align-items: stretch;
	justify-content: flex-start;
	gap: 30px;
	padding-bottom: 20px;
	border-bottom: 1px solid fade(@guidedRose, 10%);

	.brand-connect-brand {
		display: flex;
		flex-direction: row;
		align-items: stretch;
		gap: 16px;
	}

	.brand-connect-text {
		display: flex;
		flex-direction: column;
		gap: 4px;
		align-self: flex-start;
	}

	.brand-connect-brand-logo-cell {
		display: flex;
		align-items: center;
		align-self: stretch;
		min-height: 0;

		&--has-bg-colour {
			justify-content: center;
			align-self: center;
			padding: 8px;
			border-radius: 8px;

			:deep(.brand-logo) {
				margin: 0 auto;
			}
		}
	}

	.brand-connect-text--right {
		text-align: right;
		align-items: flex-end;
	}

	.brand-connect-label {
		font-size: 11px;
		text-transform: uppercase;
		letter-spacing: 0.1em;
		color: fade(@guidedRose, 70%);
		font-weight: @bold;
		span {
			color: inherit;
			display: none;
		}
	}

	.brand-connect-name {
		font-size: 18px;
		font-weight: @bold;
		margin: 0;
		color: @guidedRose;
	}
}

.brand-connect-error {
	color: @red;
	font-size: 13px;
	margin: 6px 0 0;
}

.brand-connect-load-error {
	padding: 30px 0 10px;
	text-align: center;

	p {
		margin: 0 0 14px;
		color: @guidedRose;
	}
}

.brand-connect-cache-header {
	position: absolute;
	right: 0;
	top: 6px;
	align-self: flex-start;
	display: flex;
	align-items: center;
	gap: 8px;
}

.brand-connect-cache-badge {
	position: relative;
	padding: 6px 10px;
	font-size: 11px;
	text-transform: uppercase;
	letter-spacing: 0.08em;
	font-weight: @bold;
	color: @guidedRose;
	background-color: fade(@guidedRose, 8%);
	border: 1px solid fade(@guidedRose, 12%);
	border-radius: @radiusPill;

	&.brand-connect-cache-badge--has-tooltip {
		cursor: help;
		outline: none;
	}

	.brand-connect-cache-tooltip {
		position: absolute;
		right: 0;
		top: calc(100% + 10px);
		z-index: 10;
		width: max-content;
		max-width: 320px;
		padding: 10px 12px;
		border-radius: @radiusLg;
		border: 1px solid @guidedRoseBorder;
		background: #fff;
		color: @guidedRose;
		font-size: 12px;
		font-weight: 500;
		text-transform: none;
		letter-spacing: normal;
		line-height: 1.4;
		.elevation.floatingPanel();

		opacity: 0;
		visibility: hidden;
		transform: translateY(-4px);
		transition:
			opacity 0.12s ease,
			transform 0.12s ease,
			visibility 0s linear 0.12s;

		&::before {
			content: "";
			position: absolute;
			top: -6px;
			right: 14px;
			width: 10px;
			height: 10px;
			background: #fff;
			border-left: 1px solid @guidedRoseBorder;
			border-top: 1px solid @guidedRoseBorder;
			transform: rotate(45deg);
		}
	}

	&:hover .brand-connect-cache-tooltip,
	&:focus .brand-connect-cache-tooltip,
	&:focus-within .brand-connect-cache-tooltip {
		opacity: 1;
		visibility: visible;
		transform: translateY(0);
		transition:
			opacity 0.12s ease,
			transform 0.12s ease,
			visibility 0s;
	}
}

.brand-connect-cache-controls {
	display: inline-flex;
	align-items: center;
	gap: 6px;
}

.brand-connect-cache-save {
	border: 1px solid fade(@guidedRose, 20%);
	background: #fff;
	color: @guidedRose;
	font-size: 11px;
	font-weight: @bold;
	padding: 4px 10px;
	border-radius: @radiusPill;
	cursor: pointer;
	transition:
		background-color 0.12s ease,
		border-color 0.12s ease;
}

.brand-connect-cache-save:disabled {
	opacity: 0.6;
	cursor: not-allowed;
}

.brand-connect-cache-save:hover {
	background: @guidedRoseBgStrong;
	border-color: fade(@guidedRose, 35%);
}

.brand-connect-cache-control-button {
	position: relative;
	width: 28px;
	aspect-ratio: 1 / 1;
	border-radius: @radiusPill;
	border: 1px solid @guidedRoseBorder;
	background: #fff;
	color: @guidedRose;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	padding: 0;
	cursor: pointer;
	outline: none;
	transition:
		background-color 0.12s ease,
		border-color 0.12s ease,
		opacity 0.12s ease;
}

.brand-connect-cache-control-button:hover {
	background-color: fade(@guidedRose, 8%);
	border-color: fade(@guidedRose, 35%);
}

.brand-connect-cache-control-button:hover .brand-connect-assistant-tooltip,
.brand-connect-cache-control-button:focus .brand-connect-assistant-tooltip,
.brand-connect-cache-control-button:focus-within .brand-connect-assistant-tooltip {
	opacity: 1;
	visibility: visible;
	transform: translateY(0);
	transition:
		opacity 0.12s ease,
		transform 0.12s ease,
		visibility 0s;
}

.brand-connect-cache-control-button:disabled {
	opacity: 0.45;
	cursor: not-allowed;
}

.brand-connect-cache-control-icon {
	width: 14px;
	height: 14px;
}

.feature-title-input {
	margin-bottom: 20px;
}

.brand-connect-status-note {
	margin: 0 0 10px 0;
	padding: 0;
	color: @red;
	font-size: 13px;
	font-weight: @bold;
}

.brand-connect-body {
	margin-top: 8px;
	display: flex;
	gap: 32px;
	align-items: flex-start;
	min-height: 200px;
	&--request {
		margin-top: 14px;
	}
}

.brand-connect-body-loading {
	min-height: 160px;
}

.brand-connect-main {
	flex: 1;
	width: 100%;
	position: relative;
	.brand-connect-main-title {
		font-size: 18px;
		font-weight: @bold;
		color: @guidedRose;
		margin: 0 0 16px;
	}
}

.brand-connect-request-dropdown {
	margin-bottom: 16px;
}

.brand-connect-aside-section {
	width: 100%;
	margin-top: 0;
}

.brand-connect-aside-section--requirements {
	margin-top: 26px;
	background: @surfacePanel !important;
}

.brand-connect-assistant-panel,
.brand-connect-aside-panel {
	padding: 12px 20px 20px;
	border-radius: @radiusLg;
	box-sizing: border-box;
}

.brand-connect-assistant-panel {
	--bw-accent: fade(@accentAssistTeal, 88%);
	--bw-accent-soft: fade(@accentAssistTeal, 6%);
	--bw-accent-border: fade(@accentAssistTeal, 18%);
	--bw-accent-border-strong: fade(@accentAssistTeal, 30%);
	--bw-accent-panel: fade(@accentAssistTeal, 6%);
	--bw-accent-panel-border: fade(@accentAssistTeal, 18%);
	--bw-accent-muted: fade(@accentAssistTeal, 78%);
	--bw-accent-muted-2: fade(@accentAssistTeal, 68%);
	--bw-accent-tooltip-border: fade(@accentAssistTeal, 18%);

	border: 1px solid fade(@accentAssistTeal, 18%);
	.elevation.floatingPanel();
}

.brand-connect-assistant-panel--inline {
	position: absolute;
	top: -6px;
	right: 0;
	width: auto;
	z-index: 5;
	&.brand-connect-assistant-panel--request {
		top: -14px;
		&.brand-connect-assistant-panel--floating {
			top: 20px;
		}
	}
}

.brand-connect-assistant-panel--inline.brand-connect-assistant-panel--collapsed {
	padding: 0px 14px 2px;
	cursor: pointer;
}

.brand-connect-assistant-panel--floating {
	position: fixed;
	top: 20px;
	right: 24px;
	width: 360px;
	max-height: calc(100vh - 150px);
	overflow: auto;
	z-index: 2100;
	background-color: @surfacePanel;
}

.brand-connect-assistant-loading-message {
	z-index: 2;
}

.brand-connect-aside-panel {
	border: 1px solid @guidedRoseBorder;
	background: linear-gradient(135deg, @guidedRoseBgSubtle 0%, @guidedRoseBgStrong 100%);
	.elevation.card();
}

.brand-connect-aside-panel--embedded {
	padding: 0;
	border: none;
	background: transparent !important;
	box-shadow: none;
}

.brand-connect-assistant-panel--collapsed {
	padding: 5px 20px;
}

.brand-connect-assistant-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	margin-bottom: 6px;
	flex-wrap: wrap;
}

.brand-connect-assistant-header--collapsed {
	margin-bottom: 0;
}

.brand-connect-assistant-toggle {
	border: 1px solid fade(@accentAssistTeal, 18%);
	background: #fff;
	color: fade(@accentAssistTeal, 88%);
	font-size: 12px;
	font-weight: @bold;
	padding: 4px 10px;
	border-radius: @radiusPill;
	display: inline-flex;
	align-items: center;
	gap: 6px;
	cursor: pointer;
	transition:
		background-color 0.12s ease,
		border-color 0.12s ease,
		transform 0.12s ease;
}

.brand-connect-assistant-toggle:hover,
.brand-connect-assistant-toggle:focus-visible {
	background: fade(@accentAssistTeal, 6%);
	border-color: fade(@accentAssistTeal, 30%);
}

.brand-connect-assistant-toggle-icon {
	width: 16px;
	height: 16px;
	transition: transform 0.2s ease;
}

.brand-connect-assistant-toggle[aria-expanded="true"] .brand-connect-assistant-toggle-icon {
	transform: rotate(180deg);
}

.brand-connect-assistant-close {
	display: inline-block;
	width: 2.4em;
	height: 2.4em;
	border-radius: @radiusCircle;
	border: none;
	background: white url("../../assets/img/close.svg") center center no-repeat;
	background-size: 45% auto;
	cursor: pointer;
	transition:
		background-color 0.2s ease,
		transform 0.5s ease-in-out;
	position: relative;
	z-index: 1;
	flex: 0 0 auto;

	&:hover,
	&:focus-visible {
		background-color: @controlHoverDarkTint;
		color: @textStrong;
		transform: rotate(180deg);
	}

	&:focus {
		outline: none;
	}

	&:focus-visible {
		box-shadow: 0 0 0 2px fade(@blue, 20%);
	}
}

.brand-connect-assistant-title-row {
	display: flex;
	align-items: center;
	gap: 10px;
	flex-wrap: wrap;
	padding-top: 4px;
}

.brand-connect-assistant-try-me {
	pointer-events: none;
	position: absolute;
	left: -52px;
	top: -10px;
	transform: rotate(-30deg);
	img {
		display: block;
		width: 78px;
		height: auto;
	}
}

.brand-connect-assistant-title {
	margin: 0;
	font-size: 16px;
	font-weight: @bold;
	color: fade(@accentAssistTeal, 88%);
	display: flex;
	align-items: center;
	gap: 8px;
}

.brand-connect-assistant-note {
	width: 100%;
	margin: 0;
	font-size: 13px;
	line-height: 1.4;
}

.brand-connect-assistant-hints-list {
	width: 100%;
	margin: 0 0 5px;
	padding-left: 18px;
	font-size: 13px;
	color: fade(@accentAssistTeal, 74%);
	line-height: 1.4;

	li + li {
		margin-top: 4px;
	}
}

.brand-connect-assistant-title-icon {
	width: 18px;
	height: 18px;
	color: fade(@accentAssistTeal, 88%);
	opacity: 0.8;
}

.brand-connect-assistant-link {
	border: none;
	background: transparent;
	color: fade(@accentAssistTeal, 88%);
	font-size: 12px;
	font-weight: @bold;
	padding: 0;
	cursor: pointer;
	text-decoration: underline;
}

.brand-connect-assistant-link:hover,
.brand-connect-assistant-link:focus-visible {
	color: darken(@accentAssistTeal, 8%);
}

.brand-connect-locked-notice {
	background: #fff8f0;
	border: 1.5px solid #e8d5c4;
	border-radius: @radiusMd;
	padding: 24px;
	margin: 16px 20px 12px 20px; // Match panel padding
	text-align: center;
	display: flex;
	flex-direction: column;
	gap: 14px;
	align-items: center;
	position: relative;
	z-index: 10;
	.elevation.card();
}

.brand-connect-locked-notice-text {
	margin: 0;
	font-size: 14px;
	color: #5c4a3a;
	line-height: 1.6;
	max-width: 320px;

	strong {
		color: @guidedRose;
		font-weight: @bold;
	}
}

.brand-connect-locked-notice-dismiss {
	border: none;
	background: transparent;
	color: fade(@guidedRose, 80%);
	font-size: 13px;
	font-weight: @bold;
	padding: 8px 12px;
	cursor: pointer;
	text-decoration: underline;
	transition: all 0.2s ease;
	border-radius: @radiusXs;

	&:hover,
	&:focus-visible {
		color: @guidedRose;
		background: fade(@guidedRose, 8%);
		text-decoration: none;
	}
}

.brand-connect-assistant-panel--locked {
	cursor: pointer; // Keep it clickable so they can see the locked message

	// No visual changes until they click
}

.brand-connect-assistant-panel--showing-locked-notice {
	// Remove all wrapper styling when showing the locked notice
	padding: 0 !important;
	border: none !important;
	box-shadow: none !important;
	background: transparent !important;
	cursor: default !important;
}

.brand-connect-assistant-controls {
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: 5px;
	flex: 0 0 auto;
	width: 100%;
}

.brand-connect-assistant-icon-button {
	position: relative;
	width: 28px;
	height: 28px;
	border-radius: @radiusPill;
	border: 1px solid fade(@accentAssistTeal, 18%);
	background: #fff;
	color: fade(@accentAssistTeal, 88%);
	display: inline-flex;
	align-items: center;
	justify-content: center;
	padding: 0;
	cursor: pointer;
	outline: none;
	transition:
		background-color 0.12s ease,
		border-color 0.12s ease,
		opacity 0.12s ease;
}

.brand-connect-assistant-icon-button:hover {
	background-color: fade(@accentAssistTeal, 6%);
	border-color: fade(@accentAssistTeal, 30%);
}

.brand-connect-assistant-icon-button:disabled {
	opacity: 0.45;
	cursor: not-allowed;
}

.brand-connect-assistant-icon {
	width: 14px;
	height: 14px;
}

.brand-connect-assistant-tooltip {
	position: absolute;
	right: 0;
	top: calc(100% + 10px);
	z-index: 10;
	width: max-content;
	max-width: 320px;
	padding: 10px 12px;
	border-radius: @radiusLg;
	border: 1px solid fade(@accentAssistTeal, 18%);
	background: #fff;
	color: fade(@accentAssistTeal, 88%);
	font-size: 12px;
	font-weight: 500;
	text-transform: none;
	letter-spacing: normal;
	line-height: 1.4;
	.elevation.floatingPanel();

	opacity: 0;
	visibility: hidden;
	transform: translateY(-4px);
	transition:
		opacity 0.12s ease,
		transform 0.12s ease,
		visibility 0s linear 0.12s;

	&::before {
		content: "";
		position: absolute;
		top: -6px;
		right: 10px;
		width: 10px;
		height: 10px;
		background: #fff;
		border-left: 1px solid fade(@accentAssistTeal, 18%);
		border-top: 1px solid fade(@accentAssistTeal, 18%);
		transform: rotate(45deg);
	}
}

.brand-connect-assistant-icon-button:hover .brand-connect-assistant-tooltip,
.brand-connect-assistant-icon-button:focus .brand-connect-assistant-tooltip,
.brand-connect-assistant-icon-button:focus-within .brand-connect-assistant-tooltip {
	opacity: 1;
	visibility: visible;
	transform: translateY(0);
	transition:
		opacity 0.12s ease,
		transform 0.12s ease,
		visibility 0s;
}

.brand-connect-info {
	width: 320px;
	flex: 0 0 auto;
	border: 1px solid fade(@guidedRose, 10%);
	padding: 20px;
	border-radius: @radiusLg;
	box-sizing: border-box;
	&.brand-connect-info--feature {
		background-color: @guidedRoseBgSubtle;
	}
}

.brand-connect-info-title {
	margin: 0 0 8px;
	font-size: 16px;
	font-weight: @bold;
	color: @guidedRose;
	word-break: break-all;
}

.brand-connect-info-list {
	list-style: none;
	padding: 0;
	margin: 0;
	font-size: 13px;
	color: @textGrey;
}

.brand-connect-info-item {
	margin-bottom: 4px;
}

.brand-connect-info-item--vegan {
	margin-top: 6px;
	font-weight: @bold;
	color: @guidedRose;
}

.message-label {
	display: block;
	font-size: 13px;
	font-weight: @bold;
	color: @guidedRose;
	margin-bottom: 8px;
	padding-right: 170px;
}

.message-textarea {
	width: 100%;
	min-height: 140px;
	border-radius: @radiusMd;
	border: none;
	padding: 0;
	font-size: 15px;
	line-height: 1.5;
	resize: vertical;
	color: #222222;
	background-color: #ffffff;

	&::placeholder {
		color: @guidedRoseWarningText;
	}

	&:focus {
		outline: none;
		background-color: #ffffff;
	}
}

.brand-connect-loading {
	margin-top: 16px;
	font-size: 14px;
	color: @guidedRoseWarningText;
}

.warning-message {
	font-size: 14px;
	color: @red;
	font-weight: @bold;
	margin: 0 0 10px;
}
.connect-message {
	flex-grow: 1;
	flex-shrink: 1;
	font-size: 13px;
	color: @textSubtle;
	line-height: 1.25em;
	padding: 0;
	margin: 0;

	strong {
		font-weight: @bold;
		color: @guidedRose;
	}
	&.contact-details {
		line-height: 1.5em;
	}
	&.send-request {
		margin-top: 20px;
		display: none;
	}
}
.footer-wrap {
	width: 100%;
	display: flex;
	gap: 10px;
	justify-content: flex-end;
	.connect-message {
		&.send-request {
			margin-top: 0;
			display: block;
		}
	}
}
.button-wrap {
	justify-content: flex-end;
	display: flex;
	gap: 10px;
	align-items: center;
	flex-grow: 0;
	flex-shrink: 0;
}

.ai-assist-label {
	text-align: right;
	padding: 0;
	margin: 0 0 10px;
	font-size: 12px;
	font-weight: @bold;
	color: @guidedRose;
}

.brand-connect-ai-panel {
	padding-top: 6px;
	&.brand-connect-ai-panel--loading {
		opacity: 0;
	}
}

.lifestyle-image-section {
	margin-top: 26px;
	padding-top: 26px;
	border-top: 1px solid fade(@guidedRose, 10%);

	&.mobile {
		display: none;
	}

	.lifestyle-image-title {
		font-size: 18px;
		font-weight: @bold;
		color: @guidedRose;
		margin: 0 0 16px;
	}

	.image-upload-wrap {
		display: flex;
		gap: 20px;
		align-items: center;

		img {
			max-width: 60px;
			max-height: 100px;
			object-fit: contain;
			border-radius: @radiusMd;
			border: 1px solid fade(@guidedRose, 10%);
		}

		> p {
			flex: 1;
			font-size: 13px;
			color: fade(@guidedRose, 70%);
			margin: 0;
			line-height: 1.5;
		}
	}
}

.feature-requirements-section {
	margin-top: 26px;
	padding-top: 26px;
	border-top: 1px solid fade(@guidedRose, 10%);
}

.requirements-panel {
	.requirements-title {
		font-size: 16px;
		font-weight: @bold;
		color: @guidedRose;
		margin: 0 0 16px;
	}

	.requirements-grid {
		display: grid;
		grid-template-columns: 1fr;
		gap: 20px;

		.requirements-section {
			margin-bottom: 0;
		}
	}

	.requirements-section {
		margin-bottom: 20px;

		&:last-child {
			margin-bottom: 0;
		}
	}

	.section-label {
		font-size: 13px;
		font-weight: @bold;
		margin: 0 0 8px;

		&.wants {
			color: @formDefaultColour;
		}

		&.bads {
			color: @guidedRoseText;
		}
	}

	.input-with-button {
		position: relative;

		.add-item-button {
			position: absolute;
			right: 8px;
			top: 50%;
			transform: translateY(-50%);
			background: @accentAssistTeal;
			border: none;
			border-radius: @radiusCircle;
			width: 20px;
			height: 20px;
			display: flex;
			align-items: center;
			justify-content: center;
			cursor: pointer;
			transition: all 0.2s ease;
			padding: 0;

			&:hover {
				background: darken(@accentAssistTeal, 10%);
				transform: translateY(-50%) scale(1.1);
			}

			&:active {
				transform: translateY(-50%) scale(0.95);
			}

			.plus-icon {
				width: 14px;
				height: 14px;
				color: white;
			}
		}
	}

	.hint-text {
		font-size: 11px;
		color: fade(@guidedRose, 60%);
		margin: 8px 0 0;
		line-height: 1.4;
		&.mobile-only {
			display: none;
		}
	}
}

.clickable-link {
	color: @guidedRose;
	font-weight: @bold;
	text-decoration: underline;
	cursor: pointer;
	transition: color 0.2s ease;

	&:hover {
		color: darken(@guidedRose, 15%);
	}
}

.contact-popup-title {
	font-size: 20px;
	font-weight: @bold;
	color: @guidedRose;
	margin: 0;
}

.contact-popup-content {
	.contact-popup-description {
		font-size: 14px;
		color: @textGrey;
		margin: 0 0 20px;
		line-height: 1.5;
	}

	.input-wrap-wrap {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: 16px;
		margin-bottom: 16px;
	}

	.input-wrap {
		margin-bottom: 16px;

		&:last-child {
			margin-bottom: 0;
		}
	}
}

.contact-popup-footer {
	width: 100%;
	display: flex;
	gap: 10px;
	justify-content: flex-end;
}

@media (min-width: 1024px) {
	.requirements-panel {
		.requirements-grid {
			grid-template-columns: 1fr 1fr;
			gap: 16px;
		}
	}
}

@media (max-width: 1023px) {
	.brand-connect-assistant-panel--floating {
		position: static;
		width: 100%;
		max-height: none;
		padding: 0;
		border-radius: 0;
		border: none;
		box-shadow: none;
		margin-top: 0px;
		margin-bottom: 16px;
		border-bottom: 2px solid fade(@accentAssistTeal, 18%);
		padding-bottom: 16px;
	}
}

.brand-connect-single-source-note {
	margin-top: 0;
	margin-bottom: 18px;
	padding: 18px 20px;
	border-radius: @radiusLg;
	background: fade(@accentInfoTeal, 8%);
	border: none;
	color: fade(@accentInfoTeal, 88%);
	font-size: 14px;
	font-weight: @bold;
	line-height: 1.5;
	display: flex;
	align-items: center;
	gap: 8px;
}

.brand-connect-single-source-note-icon {
	width: 20px;
	height: 20px;
	flex: 0 0 auto;
}

.brand-connect-single-source-note span {
	color: inherit;
	.clickable-link {
		text-decoration: underline;
		cursor: pointer;
	}
}

.brand-connect-readonly-text-list {
	margin: 10px 0 0;
	padding: 0;
	color: @guidedRose;
	line-height: 1.5;
}

.brand-connect-readonly-bullets {
	margin: 10px 0 0;
	padding-left: 18px;
	line-height: 1.6;

	li + li {
		margin-top: 6px;
	}
}

.brand-connect-locked-draft-content {
	margin-top: 12px;
	padding: 18px 20px;
	border-radius: @radiusLg;
	background: @guidedRoseBgSubtle;
}

.brand-connect-locked-draft-empty-state {
	margin-top: 12px;
	padding: 18px 20px;
	border-radius: @radiusLg;
	background: @guidedRoseBgSubtle;
	color: @guidedRoseWarningText;
	font-size: 14px;
	line-height: 1.6;
	text-align: left;
	font-weight: @bold;
}

.brand-connect-locked-draft-content .requirements-panel .section-label.bads {
	color: @guidedRose;
}

@media (max-width: 767px) {
	.brand-connect-main {
		padding-right: 0;
	}

	.brand-logo {
		width: 100px;
	}

	.brand-connect-header-title {
		display: none;
	}

	.requirements-panel {
		.hint-text.mobile-only {
			display: block;
		}
	}

	.brand-connect-status-note {
		margin-top: 40px;
	}

	.brand-connect-assistant-panel--inline {
		margin-bottom: 16px;
		margin-top: 8px;
	}

	.lifestyle-image-section {
		&.mobile {
			display: block;
			margin-top: 14px;
		}
		&.desktop {
			display: none;
		}

		.image-upload-wrap {
			display: grid;
			grid-template-columns: minmax(0, 1fr) auto;
			column-gap: 20px;
			row-gap: 8px;
			align-items: flex-start;

			.button {
				max-width: 200px;
			}

			> p {
				grid-column: 1;
				width: 100%;
				flex: none;
			}

			img {
				grid-column: 2;
				grid-row: 1 / span 2;
				justify-self: end;
			}

			&:not(:has(img)) {
				grid-template-columns: 1fr;
			}
		}
	}
	.brand-connect-body {
		flex-direction: column;
		gap: 20px;
		margin-top: 24px;
		&--request {
			margin-top: 30px;
			.message-label {
				padding-right: 0;
			}
			.brand-connect-assistant-panel--inline.brand-connect-assistant-panel--request {
				top: -36px;
				margin: 0;
				&.brand-connect-assistant-panel--floating {
					margin: 0 0 30px;
				}
			}
		}
	}

	.brand-connect-cache-badge {
		display: none;
	}

	.brand-connect-header {
		border-bottom: none;
		padding-bottom: 0;
		min-height: 44px;
		.brand-connect-label {
			span {
				display: inline;
			}
		}

		.brand-connect-brand {
			gap: 4px;
		}
		.brand-connect-brand {
			&.brand-connect-brand--connector {
				display: none;
			}
		}
	}

	.brand-connect-info {
		width: 100%;
	}
	.footer-wrap {
		flex-wrap: wrap;
		.connect-message {
			&.send-request {
				display: none;
			}
		}
	}
	.button-wrap {
		justify-content: space-between;
		margin-top: 0;
		width: 100%;
	}
	.connect-message {
		line-height: 1.5em;
		&.send-request {
			display: block;
		}
	}

	.contact-popup-content {
		.input-wrap-wrap {
			grid-template-columns: 1fr;
		}
	}
	.brand-connect-cache-control-button {
		width: 34px;
		&:last-child {
			display: none;
		}
	}
	.brand-connect-cache-header {
		top: 0;
	}

	.brand-connect-cache-controls {
		gap: 14px;
	}
}
@media (max-width: 1600px) {
	:deep(.modal.brand-connect-main-pupup .modal-wrap .modal-inner) {
		margin: 0 !important;
	}
}

@media (min-width: 1600px) {
	.brand-connect-assistant-panel--floating {
		right: -775px;
		margin-right: 50%;
	}
}
</style>
<style lang="less">
@media (max-width: 767px) {
	.modal.brand-connect-main-pupup.pitch-assistant-open .modal-wrap {
		.brand-connect-body--request {
			margin-top: 0;
		}
	}
}
@media (min-width: 1024px) and (max-width: 1600px) {
	.modal.brand-connect-main-pupup.pitch-assistant-open .modal-wrap {
		padding-right: 400px;
		.modal-inner {
			margin: 0 0 0 auto;
		}
	}
}
</style>
